// Procedural voxel fighter planes — chunky boxes only, no asset files.
// Models are built nose-along-+X / up-+Y / right-wing-+Z so server attitude
// quaternions apply directly to the mesh. Each aircraft class gets its own
// silhouette so you can read the matchup at a glance.

import * as THREE from "three";

export const TEAM_CRIMSON = 0;
export const TEAM_COBALT = 1;

export const TEAM_INFO = [
  { name: "CRIMSON", color: 0xe84a4a, body: 0xb23a3a, accent: 0xf2e3c8, cssColor: "#e84a4a" },
  { name: "COBALT", color: 0x4a7de8, body: 0x3a5cb2, accent: 0xdce8f2, cssColor: "#4a7de8" },
];

/** Merge axis-aligned colored boxes into one vertex-colored geometry.
 *  An entry may instead be a smooth cone/cylinder segment lying along +X:
 *  { cone: true, x,y,z, len, r0, r1, seg?, color } — r0 is the aft (-X)
 *  radius, r1 the forward (+X) radius (0 for a sharp tip). Used sparingly
 *  (the jet's radome, tip tanks) so the chunky-box look stays the rule. */
export function mergeBoxes(boxes) {
  const positions = [];
  const normals = [];
  const colors = [];
  const c = new THREE.Color();
  for (const b of boxes) {
    let g;
    if (b.cone) {
      // CylinderGeometry's +Y top carries radiusTop; rotate +Y onto +X so
      // r1 faces forward along the nose line.
      g = new THREE.CylinderGeometry(Math.max(b.r1 ?? 0, 0.01), b.r0, b.len, b.seg ?? 10).toNonIndexed();
      g.rotateZ(-Math.PI / 2);
    } else {
      g = new THREE.BoxGeometry(b.w, b.h, b.d).toNonIndexed();
    }
    g.translate(b.x, b.y, b.z);
    const pos = g.getAttribute("position");
    const nor = g.getAttribute("normal");
    c.set(b.color);
    for (let i = 0; i < pos.count; i++) {
      positions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
      normals.push(nor.getX(i), nor.getY(i), nor.getZ(i));
      colors.push(c.r, c.g, c.b);
    }
    g.dispose();
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  return geo;
}

const dark = 0x2b2f36;
const glass = 0x9fd8f0;
const glassDeep = 0x6fb6d8;
const gunmetal = 0x1c1e22;
const steel = 0x6a6f78;
const exhaustBrown = 0x4a3a2c;
const tire = 0x16171a;
const navRed = 0xff4444;
const navGreen = 0x35e06a;

/** Darken/lighten a hex color by factor k (panel shading, ailerons, bellies). */
function shade(hex, k) {
  const c = new THREE.Color(hex).multiplyScalar(k);
  return c.getHex();
}

/** Duplicate boxes across the z=0 plane (port/starboard symmetry). */
const mirrorZ = (boxes) => boxes.flatMap((b) => [b, { ...b, z: -b.z }]);

// --- class silhouettes ---------------------------------------------------
// Still chunky boxes only, but with tapered fuselages, framed canopies,
// engine details, roundels, control-surface accents, and nav lights so the
// planes read as machines rather than bricks.

function sparrowhawkBoxes(t) {
  return [
    // Fuselage, tapering toward the tail, with a darker belly line.
    { x: 0.7, y: 0, z: 0, w: 4.6, h: 1.3, d: 1.4, color: t.body },
    { x: -2.2, y: 0.06, z: 0, w: 1.7, h: 1.08, d: 1.12, color: t.body },
    { x: -3.5, y: 0.12, z: 0, w: 1.3, h: 0.84, d: 0.88, color: t.body },
    { x: 0.7, y: -0.6, z: 0, w: 4.2, h: 0.28, d: 1.1, color: shade(t.body, 0.72) },
    // Radial cowl, cooling-flap ring, lip, and spinner.
    { x: 3.45, y: 0, z: 0, w: 1.5, h: 1.5, d: 1.6, color: dark },
    { x: 2.78, y: 0, z: 0, w: 0.18, h: 1.56, d: 1.66, color: gunmetal }, // cowl flaps
    { x: 4.25, y: 0, z: 0, w: 0.3, h: 1.3, d: 1.4, color: gunmetal },
    { x: 4.45, y: 0, z: 0, w: 0.55, h: 0.6, d: 0.6, color: steel },
    ...mirrorZ([
      { x: 2.65, y: -0.18, z: 0.78, w: 1.5, h: 0.28, d: 0.18, color: exhaustBrown }, // exhausts
      // Quad battery: two fifties a foot apart on each wing.
      { x: 2.05, y: -0.3, z: 2.95, w: 1.1, h: 0.22, d: 0.15, color: gunmetal }, // wing guns
      { x: 2.88, y: -0.3, z: 2.95, w: 0.55, h: 0.12, d: 0.12, color: steel }, // barrel tips
      { x: 2.05, y: -0.3, z: 3.25, w: 1.1, h: 0.22, d: 0.15, color: gunmetal },
      { x: 2.88, y: -0.3, z: 3.25, w: 0.55, h: 0.12, d: 0.12, color: steel },
    ]),
    { x: 1.3, y: -0.28, z: 4.6, w: 1.0, h: 0.07, d: 0.07, color: steel }, // pitot tube
    { x: 2.9, y: -0.72, z: 0, w: 1.2, h: 0.34, d: 0.7, color: dark }, // chin intake
    // Canopy: windscreen frame, glasshouse, rear frame, headrest fairing.
    { x: 1.55, y: 0.86, z: 0, w: 0.3, h: 0.55, d: 1.05, color: t.body },
    { x: 0.85, y: 0.95, z: 0, w: 1.3, h: 0.7, d: 0.95, color: glass },
    { x: 0.16, y: 0.84, z: 0, w: 0.24, h: 0.5, d: 1.0, color: t.body },
    { x: -0.75, y: 0.7, z: 0, w: 1.6, h: 0.46, d: 0.78, color: t.body },
    { x: 0.85, y: 1.32, z: 0, w: 1.34, h: 0.1, d: 0.5, color: glassDeep }, // canopy crown
    { x: 1.0, y: 1.45, z: 0, w: 0.1, h: 0.45, d: 0.1, color: dark }, // antenna mast
    // Turtledeck spine tapering from the headrest down to the tail.
    { x: -2.25, y: 0.66, z: 0, w: 1.5, h: 0.22, d: 0.62, color: t.body },
    { x: -3.45, y: 0.56, z: 0, w: 1.1, h: 0.18, d: 0.5, color: t.body },
    // Wings: three stacked slabs of shrinking thickness fake a real taper.
    { x: 0.5, y: -0.42, z: 0, w: 2.7, h: 0.4, d: 7.6, color: t.color },
    { x: 0.46, y: -0.4, z: 0, w: 2.45, h: 0.33, d: 9.4, color: t.color },
    { x: 0.42, y: -0.36, z: 0, w: 2.2, h: 0.3, d: 11.0, color: t.color },
    ...mirrorZ([
      { x: 0.4, y: -0.34, z: 5.3, w: 1.5, h: 0.36, d: 0.6, color: t.accent }, // tip caps
      { x: 0.7, y: -0.14, z: 3.3, w: 1.5, h: 0.1, d: 1.5, color: t.accent }, // roundel
      { x: 0.7, y: -0.06, z: 3.3, w: 0.9, h: 0.1, d: 0.9, color: t.color },
      { x: -0.55, y: -0.34, z: 3.9, w: 0.5, h: 0.26, d: 2.4, color: shade(t.color, 0.78) }, // aileron
    ]),
    { x: 0.4, y: -0.26, z: 5.66, w: 0.26, h: 0.26, d: 0.26, color: navGreen },
    { x: 0.4, y: -0.26, z: -5.66, w: 0.26, h: 0.26, d: 0.26, color: navRed },
    // Tailplane with elevator strip and tip caps, fin fillet, fin + rudder.
    { x: -4.0, y: 0.35, z: 0, w: 1.3, h: 0.24, d: 4.4, color: t.color },
    { x: -4.62, y: 0.35, z: 0, w: 0.34, h: 0.2, d: 4.0, color: shade(t.color, 0.78) },
    ...mirrorZ([{ x: -4.05, y: 0.35, z: 2.0, w: 1.1, h: 0.26, d: 0.42, color: t.accent }]),
    { x: -3.3, y: 0.58, z: 0, w: 1.3, h: 0.3, d: 0.22, color: t.body }, // fin fillet
    { x: -3.95, y: 1.1, z: 0, w: 1.0, h: 1.5, d: 0.26, color: t.body },
    { x: -4.5, y: 1.2, z: 0, w: 0.5, h: 1.55, d: 0.24, color: t.color },
    { x: -4.5, y: 1.0, z: 0, w: 0.46, h: 0.34, d: 0.3, color: t.accent }, // rudder stripe
    { x: -4.05, y: 1.92, z: 0, w: 0.7, h: 0.26, d: 0.3, color: t.accent }, // fin tip
    { x: -3.9, y: -0.5, z: 0, w: 0.28, h: 0.46, d: 0.2, color: tire }, // tailwheel
  ];
}

// Long-nosed, racy, with a bubble canopy set far back and clipped wings.
function thunderheadBoxes(t) {
  return [
    // Long inline-engine fuselage with belly shading and rear taper.
    { x: 1.0, y: 0, z: 0, w: 6.2, h: 1.25, d: 1.3, color: t.body },
    { x: -2.9, y: 0.07, z: 0, w: 1.9, h: 1.0, d: 1.05, color: t.body },
    { x: -4.2, y: 0.14, z: 0, w: 1.0, h: 0.78, d: 0.84, color: t.body },
    { x: 1.0, y: -0.56, z: 0, w: 5.6, h: 0.26, d: 1.0, color: shade(t.body, 0.72) },
    // Pointed cowl with flap ring, lip, spinner; stacked nose exhausts.
    { x: 4.45, y: -0.02, z: 0, w: 1.4, h: 1.4, d: 1.45, color: dark },
    { x: 3.85, y: -0.02, z: 0, w: 0.2, h: 1.52, d: 1.56, color: gunmetal }, // cowl flaps
    { x: 5.15, y: 0, z: 0, w: 0.3, h: 1.15, d: 1.2, color: gunmetal },
    { x: 5.4, y: 0, z: 0, w: 0.6, h: 0.65, d: 0.65, color: steel },
    ...mirrorZ([
      { x: 3.55, y: 0.34, z: 0.62, w: 1.7, h: 0.2, d: 0.16, color: exhaustBrown },
      // The 20 mm pair: one hispano on each wing — the cannon doctrine
      // stops at two barrels, the punch is in the shell.
      { x: 3.3, y: -0.25, z: 3.2, w: 1.2, h: 0.26, d: 0.18, color: gunmetal }, // wing guns
      { x: 4.15, y: -0.25, z: 3.2, w: 0.6, h: 0.13, d: 0.13, color: steel }, // barrel tips
    ]),
    { x: 1.1, y: -0.26, z: 4.8, w: 1.0, h: 0.07, d: 0.07, color: steel }, // pitot tube
    { x: 4.0, y: -0.76, z: 0, w: 1.4, h: 0.38, d: 0.8, color: dark }, // chin scoop
    { x: -0.5, y: -0.82, z: 0, w: 2.2, h: 0.46, d: 0.9, color: dark }, // belly radiator
    { x: 0.65, y: -0.8, z: 0, w: 0.3, h: 0.4, d: 0.8, color: gunmetal },
    // Bubble canopy: framed windscreen, two stacked glass tiers.
    { x: 0.62, y: 0.78, z: 0, w: 0.26, h: 0.5, d: 1.0, color: t.body },
    { x: -0.35, y: 0.85, z: 0, w: 1.7, h: 0.55, d: 1.0, color: glass },
    { x: -0.45, y: 1.22, z: 0, w: 1.15, h: 0.34, d: 0.78, color: glassDeep },
    // Racing stripe down the spine; turtledeck fairing aft of the canopy.
    { x: 1.6, y: 0.66, z: 0, w: 5.6, h: 0.12, d: 0.5, color: t.accent },
    { x: -1.9, y: 0.62, z: 0, w: 1.7, h: 0.22, d: 0.6, color: t.body },
    { x: -3.3, y: 0.54, z: 0, w: 1.2, h: 0.18, d: 0.48, color: t.body },
    // Clipped wings (three taper slabs), roundels, ailerons, tip lights.
    { x: 0.8, y: -0.36, z: 0, w: 2.3, h: 0.34, d: 8.4, color: t.color },
    { x: 0.62, y: -0.34, z: 0, w: 1.95, h: 0.3, d: 9.9, color: t.color },
    { x: 0.5, y: -0.32, z: 0, w: 1.6, h: 0.26, d: 11.4, color: t.color },
    ...mirrorZ([
      { x: 0.5, y: -0.3, z: 5.5, w: 1.2, h: 0.3, d: 0.5, color: t.accent },
      { x: 0.8, y: -0.12, z: 3.5, w: 1.4, h: 0.1, d: 1.4, color: t.accent },
      { x: 0.8, y: -0.04, z: 3.5, w: 0.85, h: 0.1, d: 0.85, color: t.color },
      { x: -0.42, y: -0.3, z: 4.2, w: 0.46, h: 0.22, d: 2.3, color: shade(t.color, 0.78) },
    ]),
    { x: 0.5, y: -0.22, z: 5.8, w: 0.26, h: 0.26, d: 0.26, color: navGreen },
    { x: 0.5, y: -0.22, z: -5.8, w: 0.26, h: 0.26, d: 0.26, color: navRed },
    // Tail group with fin fillet and elevator tip caps.
    { x: -4.5, y: 0.32, z: 0, w: 1.2, h: 0.22, d: 3.9, color: t.color },
    { x: -5.05, y: 0.32, z: 0, w: 0.3, h: 0.18, d: 3.5, color: shade(t.color, 0.78) },
    ...mirrorZ([{ x: -4.55, y: 0.32, z: 1.78, w: 1.0, h: 0.24, d: 0.38, color: t.accent }]),
    { x: -3.7, y: 0.55, z: 0, w: 1.3, h: 0.28, d: 0.2, color: t.body }, // fin fillet
    { x: -4.3, y: 0.95, z: 0, w: 0.9, h: 1.3, d: 0.24, color: t.body },
    { x: -4.8, y: 1.05, z: 0, w: 0.46, h: 1.45, d: 0.22, color: t.color },
    { x: -4.8, y: 0.85, z: 0, w: 0.42, h: 0.3, d: 0.28, color: t.accent },
    { x: -4.35, y: 1.66, z: 0, w: 0.6, h: 0.22, d: 0.28, color: t.accent },
    { x: -4.1, y: -0.42, z: 0, w: 0.28, h: 0.44, d: 0.2, color: tire },
  ];
}

// Twin-engine slab with wing nacelles and a glass nose. Reads HEAVY.
function ironcladBoxes(t) {
  return [
    // Slab fuselage with shaded belly and bomb-bay doors.
    { x: 0.2, y: 0, z: 0, w: 7.2, h: 1.7, d: 1.8, color: t.body },
    { x: 0.1, y: -0.92, z: 0, w: 6.2, h: 0.3, d: 1.5, color: shade(t.body, 0.72) },
    { x: 0.4, y: -1.04, z: 0, w: 2.6, h: 0.18, d: 1.1, color: gunmetal },
    // Framed bombardier glass nose + chin cannons.
    { x: 3.95, y: 0.1, z: 0, w: 1.1, h: 1.25, d: 1.35, color: glass },
    { x: 3.95, y: 0.12, z: 0, w: 1.2, h: 0.16, d: 1.45, color: t.body },
    { x: 4.55, y: 0.02, z: 0, w: 0.3, h: 0.9, d: 1.0, color: glassDeep },
    // The single 30 mm cannon on the centerline, slung in a chin fairing
    // under the bombardier's glass (a twin has no central spinner to fire
    // through) — one heavy barrel is the whole battery, streaming at the
    // full per-round rate: the jackpot every gunner hunts, dealt one card
    // at a time.
    { x: 4.3, y: -0.62, z: 0, w: 1.3, h: 0.32, d: 0.32, color: gunmetal },
    { x: 5.2, y: -0.62, z: 0, w: 0.75, h: 0.18, d: 0.18, color: steel }, // barrel
    // Stepped cockpit: windscreen frame, glasshouse, side panes, roof rail.
    { x: 1.75, y: 1.0, z: 0, w: 0.3, h: 0.66, d: 1.3, color: t.body },
    { x: 0.9, y: 1.1, z: 0, w: 1.7, h: 0.8, d: 1.2, color: glass },
    ...mirrorZ([{ x: 0.9, y: 1.05, z: 0.64, w: 1.5, h: 0.5, d: 0.1, color: glassDeep }]),
    { x: 0.9, y: 1.54, z: 0, w: 1.8, h: 0.1, d: 1.28, color: t.body },
    { x: 1.7, y: 1.75, z: 0, w: 0.1, h: 0.5, d: 0.1, color: dark },
    // Dorsal turret: steel ring, glass dome, twin guns.
    { x: -1.5, y: 0.98, z: 0, w: 1.1, h: 0.34, d: 1.1, color: steel },
    { x: -1.5, y: 1.26, z: 0, w: 0.85, h: 0.44, d: 0.85, color: glassDeep },
    ...mirrorZ([{ x: -0.85, y: 1.3, z: 0.18, w: 1.1, h: 0.12, d: 0.12, color: gunmetal }]),
    // Broad three-slab tapered wing, roundels, ailerons, tip caps + lights.
    { x: 0.2, y: -0.52, z: 0, w: 3.1, h: 0.42, d: 9.6, color: t.color },
    { x: 0.15, y: -0.49, z: 0, w: 2.75, h: 0.37, d: 12.0, color: t.color },
    { x: 0.1, y: -0.46, z: 0, w: 2.4, h: 0.32, d: 14.2, color: t.color },
    { x: 1.1, y: -0.34, z: 6.0, w: 1.0, h: 0.07, d: 0.07, color: steel }, // pitot tube
    ...mirrorZ([
      { x: 0.5, y: -0.24, z: 5.2, w: 1.6, h: 0.1, d: 1.6, color: t.accent },
      { x: 0.5, y: -0.16, z: 5.2, w: 1.0, h: 0.1, d: 1.0, color: t.color },
      { x: -1.25, y: -0.44, z: 5.7, w: 0.5, h: 0.24, d: 2.6, color: shade(t.color, 0.78) },
      { x: 0.1, y: -0.42, z: 6.9, w: 1.3, h: 0.38, d: 0.6, color: t.accent },
      // Nacelles: cowl, flap ring, lip, spinner hub, tail cone, exhaust.
      { x: 1.2, y: -0.34, z: 3.6, w: 2.8, h: 1.25, d: 1.25, color: dark },
      { x: 1.95, y: -0.34, z: 3.6, w: 0.22, h: 1.39, d: 1.39, color: gunmetal },
      { x: 2.65, y: -0.32, z: 3.6, w: 0.3, h: 1.05, d: 1.05, color: gunmetal },
      { x: 2.88, y: -0.32, z: 3.6, w: 0.5, h: 0.55, d: 0.55, color: steel },
      { x: -0.45, y: -0.4, z: 3.6, w: 1.0, h: 0.9, d: 0.95, color: shade(0x2b2f36, 1.3) },
      { x: 0.9, y: -0.18, z: 4.28, w: 1.2, h: 0.2, d: 0.16, color: exhaustBrown },
    ]),
    { x: 0.1, y: -0.34, z: 7.25, w: 0.28, h: 0.28, d: 0.28, color: navGreen },
    { x: 0.1, y: -0.34, z: -7.25, w: 0.28, h: 0.28, d: 0.28, color: navRed },
    // Tail: shoulder fairing, tapered boom + cone, broad tailplane, twin fins.
    { x: -3.1, y: 0.55, z: 0, w: 1.6, h: 0.5, d: 1.45, color: t.body },
    { x: -3.7, y: 0.22, z: 0, w: 2.2, h: 1.15, d: 1.25, color: t.body },
    { x: -4.95, y: 0.24, z: 0, w: 0.7, h: 0.85, d: 0.95, color: shade(t.body, 0.9) },
    { x: -4.55, y: 0.45, z: 0, w: 1.4, h: 0.28, d: 5.8, color: t.color },
    { x: -5.18, y: 0.45, z: 0, w: 0.36, h: 0.22, d: 5.2, color: shade(t.color, 0.78) },
    ...mirrorZ([
      { x: -4.4, y: 1.25, z: 2.7, w: 1.0, h: 1.55, d: 0.3, color: t.color },
      { x: -4.85, y: 1.25, z: 2.7, w: 0.36, h: 1.3, d: 0.34, color: t.accent },
    ]),
    { x: -3.6, y: -0.6, z: 0, w: 0.3, h: 0.5, d: 0.22, color: tire },
  ];
}

// Four-engine heavy bomber: long slab fuselage, glass bombardier nose,
// huge straight wing with four nacelles, tall single fin, tail-gun glass.
function leviathanBoxes(t) {
  return [
    // Long fuselage with shaded belly and open-looking bomb-bay strake.
    { x: 0.0, y: 0, z: 0, w: 10.4, h: 2.0, d: 2.1, color: t.body },
    { x: -6.0, y: 0.15, z: 0, w: 2.4, h: 1.5, d: 1.6, color: t.body },
    { x: -0.4, y: -1.08, z: 0, w: 8.6, h: 0.34, d: 1.7, color: shade(t.body, 0.72) },
    { x: 0.6, y: -1.24, z: 0, w: 4.2, h: 0.2, d: 1.3, color: gunmetal }, // bomb-bay doors
    // Framed bombardier glass nose.
    { x: 5.5, y: 0.12, z: 0, w: 1.3, h: 1.5, d: 1.6, color: glass },
    { x: 5.5, y: 0.14, z: 0, w: 1.4, h: 0.18, d: 1.7, color: t.body },
    { x: 6.2, y: 0.02, z: 0, w: 0.34, h: 1.05, d: 1.15, color: glassDeep },
    ...mirrorZ([
      { x: 5.9, y: -0.3, z: 0.9, w: 1.0, h: 0.2, d: 0.2, color: gunmetal }, // chin guns
      { x: 6.6, y: -0.3, z: 0.9, w: 0.5, h: 0.14, d: 0.14, color: steel }, // barrel tips
      { x: 4.0, y: 0.5, z: 1.0, w: 1.3, h: 0.4, d: 0.14, color: glassDeep }, // nose side panes
    ]),
    // Stepped flight deck high on the nose; spine fairing running aft.
    { x: 3.4, y: 1.22, z: 0, w: 0.34, h: 0.7, d: 1.5, color: t.body },
    { x: 2.45, y: 1.3, z: 0, w: 1.8, h: 0.85, d: 1.4, color: glass },
    { x: 2.45, y: 1.78, z: 0, w: 1.9, h: 0.12, d: 1.48, color: t.body },
    { x: 3.2, y: 2.0, z: 0, w: 0.1, h: 0.55, d: 0.1, color: dark }, // antenna
    { x: 0.6, y: 1.06, z: 0, w: 2.4, h: 0.18, d: 1.1, color: t.body },
    { x: -4.3, y: 0.95, z: 0, w: 2.0, h: 0.16, d: 0.85, color: t.body },
    // Dorsal turret ring amidships (the rotating dome + guns are a separate
    // articulated mount — see CLASS_TURRETS / PlaneVisual.setTurretAim).
    { x: -1.6, y: 1.18, z: 0, w: 1.2, h: 0.36, d: 1.2, color: steel },
    // Tail-gunner glasshouse and waist-gun blisters (the twin tail stingers
    // are articulated too).
    { x: -7.35, y: 0.3, z: 0, w: 0.9, h: 1.0, d: 1.1, color: glassDeep },
    ...mirrorZ([
      { x: -3.4, y: 0.05, z: 1.04, w: 0.9, h: 0.6, d: 0.16, color: glassDeep },
    ]),
    // Huge straight wing: three stacked slabs of shrinking thickness.
    { x: 0.6, y: -0.5, z: 0, w: 3.6, h: 0.5, d: 12.5, color: t.color },
    { x: 0.5, y: -0.46, z: 0, w: 3.25, h: 0.43, d: 16.2, color: t.color },
    { x: 0.4, y: -0.42, z: 0, w: 2.9, h: 0.36, d: 19.6, color: t.color },
    { x: 1.9, y: -0.34, z: 8.6, w: 1.1, h: 0.08, d: 0.08, color: steel }, // pitot tube
    ...mirrorZ([
      { x: 0.8, y: -0.2, z: 7.0, w: 1.9, h: 0.1, d: 1.9, color: t.accent }, // roundel
      { x: 0.8, y: -0.12, z: 7.0, w: 1.15, h: 0.1, d: 1.15, color: t.color },
      { x: -1.2, y: -0.4, z: 8.2, w: 0.55, h: 0.26, d: 3.0, color: shade(t.color, 0.78) }, // aileron
      { x: 0.4, y: -0.38, z: 9.5, w: 1.5, h: 0.42, d: 0.7, color: t.accent }, // tip caps
      // Inboard + outboard nacelles: cowl, flap ring, lip, hub, tail cone.
      { x: 1.7, y: -0.4, z: 2.9, w: 3.0, h: 1.35, d: 1.35, color: dark },
      { x: 2.45, y: -0.4, z: 2.9, w: 0.22, h: 1.49, d: 1.49, color: gunmetal },
      { x: 3.25, y: -0.38, z: 2.9, w: 0.3, h: 1.1, d: 1.1, color: gunmetal },
      { x: 3.5, y: -0.38, z: 2.9, w: 0.5, h: 0.6, d: 0.6, color: steel },
      { x: 0.0, y: -0.46, z: 2.9, w: 1.0, h: 0.95, d: 1.0, color: shade(0x2b2f36, 1.3) },
      { x: 1.5, y: -0.24, z: 3.6, w: 1.2, h: 0.2, d: 0.16, color: exhaustBrown },
      { x: 1.3, y: -0.4, z: 5.7, w: 2.7, h: 1.2, d: 1.2, color: dark },
      { x: 1.95, y: -0.4, z: 5.7, w: 0.22, h: 1.34, d: 1.34, color: gunmetal },
      { x: 2.7, y: -0.38, z: 5.7, w: 0.3, h: 1.0, d: 1.0, color: gunmetal },
      { x: 2.93, y: -0.38, z: 5.7, w: 0.5, h: 0.55, d: 0.55, color: steel },
      { x: -0.2, y: -0.46, z: 5.7, w: 0.9, h: 0.85, d: 0.9, color: shade(0x2b2f36, 1.3) },
    ]),
    { x: 0.4, y: -0.28, z: 10.0, w: 0.3, h: 0.3, d: 0.3, color: navGreen },
    { x: 0.4, y: -0.28, z: -10.0, w: 0.3, h: 0.3, d: 0.3, color: navRed },
    // Tail: broad tailplane with tip caps, towering single fin + fillet.
    { x: -6.3, y: 0.55, z: 0, w: 1.7, h: 0.3, d: 7.4, color: t.color },
    { x: -7.05, y: 0.55, z: 0, w: 0.4, h: 0.24, d: 6.6, color: shade(t.color, 0.78) },
    ...mirrorZ([{ x: -6.35, y: 0.55, z: 3.42, w: 1.3, h: 0.28, d: 0.5, color: t.accent }]),
    { x: -5.0, y: 0.95, z: 0, w: 1.6, h: 0.35, d: 0.3, color: t.body }, // fin fillet
    { x: -6.0, y: 1.7, z: 0, w: 1.5, h: 2.4, d: 0.34, color: t.body },
    { x: -6.9, y: 1.85, z: 0, w: 0.6, h: 2.5, d: 0.3, color: t.color },
    { x: -6.9, y: 1.35, z: 0, w: 0.55, h: 0.45, d: 0.38, color: t.accent }, // rudder stripe
    { x: -6.1, y: 2.95, z: 0, w: 1.0, h: 0.3, d: 0.4, color: t.accent }, // fin tip
    { x: -5.6, y: -0.7, z: 0, w: 0.34, h: 0.55, d: 0.26, color: tire }, // tailwheel
  ];
}

// Swordfin torpedo bomber: a stout single-engine machine with a long
// greenhouse canopy, cranked gull wings, and the fish slung under the belly.
function swordfinBoxes(t) {
  return [
    // Deep fuselage with a shaded belly and tapered tail.
    { x: 0.4, y: 0, z: 0, w: 6.4, h: 1.7, d: 1.7, color: t.body },
    { x: -3.6, y: 0.1, z: 0, w: 2.2, h: 1.2, d: 1.3, color: t.body },
    { x: 0.4, y: -0.85, z: 0, w: 6.0, h: 0.3, d: 1.4, color: shade(t.body, 0.72) },
    // Radial cowl, flap ring, spinner.
    { x: 3.9, y: 0, z: 0, w: 1.6, h: 1.8, d: 1.9, color: dark },
    { x: 3.15, y: 0, z: 0, w: 0.2, h: 1.86, d: 1.96, color: gunmetal },
    { x: 4.75, y: 0, z: 0, w: 0.5, h: 0.7, d: 0.7, color: steel },
    ...mirrorZ([{ x: 3.4, y: -0.5, z: 0.7, w: 1.2, h: 0.24, d: 0.18, color: exhaustBrown }]),
    // Long greenhouse canopy for pilot + torpedo officer.
    { x: 1.0, y: 1.0, z: 0, w: 3.4, h: 0.7, d: 1.2, color: glass },
    { x: 1.0, y: 1.36, z: 0, w: 3.5, h: 0.12, d: 1.26, color: t.body },
    { x: 2.0, y: 1.0, z: 0, w: 0.2, h: 0.74, d: 1.26, color: t.body }, // frame
    { x: 0.0, y: 1.0, z: 0, w: 0.2, h: 0.74, d: 1.26, color: t.body },
    // The fish, slung on belly crutches.
    { x: 0.6, y: -1.25, z: 0, w: 4.6, h: 0.62, d: 0.62, color: 0x23262c },
    { x: 2.9, y: -1.25, z: 0, w: 0.5, h: 0.5, d: 0.5, color: steel }, // nose cap
    { x: -1.8, y: -1.22, z: 0, w: 0.5, h: 0.7, d: 0.7, color: gunmetal }, // screws
    { x: 1.6, y: -0.95, z: 0, w: 0.3, h: 0.3, d: 0.74, color: gunmetal }, // crutch
    { x: -0.6, y: -0.95, z: 0, w: 0.3, h: 0.3, d: 0.74, color: gunmetal },
    // Cranked gull wing: inner anhedral slab, outer span, tip caps.
    { x: 0.6, y: -0.6, z: 0, w: 2.9, h: 0.4, d: 5.4, color: t.color },
    { x: 0.5, y: -0.3, z: 0, w: 2.7, h: 0.34, d: 11.6, color: t.color },
    ...mirrorZ([
      // Quad battery: two fifties a foot apart on each outer wing panel.
      { x: 2.0, y: -0.42, z: 3.25, w: 1.0, h: 0.22, d: 0.15, color: gunmetal }, // wing guns
      { x: 2.7, y: -0.42, z: 3.25, w: 0.55, h: 0.12, d: 0.12, color: steel }, // barrel tips
      { x: 2.0, y: -0.42, z: 3.55, w: 1.0, h: 0.22, d: 0.15, color: gunmetal },
      { x: 2.7, y: -0.42, z: 3.55, w: 0.55, h: 0.12, d: 0.12, color: steel },
    ]),
    ...mirrorZ([
      { x: 0.6, y: -0.1, z: 4.4, w: 1.6, h: 0.1, d: 1.6, color: t.accent }, // roundel
      { x: 0.6, y: -0.04, z: 4.4, w: 0.95, h: 0.1, d: 0.95, color: t.color },
      { x: -0.7, y: -0.26, z: 5.0, w: 0.5, h: 0.22, d: 2.2, color: shade(t.color, 0.78) },
      { x: 0.4, y: -0.24, z: 5.6, w: 1.3, h: 0.38, d: 0.6, color: t.accent }, // tip caps
    ]),
    { x: 0.4, y: -0.16, z: 5.9, w: 0.26, h: 0.26, d: 0.26, color: navGreen },
    { x: 0.4, y: -0.16, z: -5.9, w: 0.26, h: 0.26, d: 0.26, color: navRed },
    // Tail: broad tailplane, tall rounded fin with rudder stripe.
    { x: -4.4, y: 0.5, z: 0, w: 1.5, h: 0.26, d: 4.6, color: t.color },
    ...mirrorZ([{ x: -4.45, y: 0.5, z: 2.1, w: 1.2, h: 0.24, d: 0.45, color: t.accent }]),
    { x: -4.2, y: 1.3, z: 0, w: 1.3, h: 1.7, d: 0.3, color: t.body },
    { x: -4.95, y: 1.4, z: 0, w: 0.5, h: 1.8, d: 0.26, color: t.color },
    { x: -4.95, y: 0.95, z: 0, w: 0.45, h: 0.4, d: 0.32, color: t.accent },
    { x: -4.3, y: 2.25, z: 0, w: 0.8, h: 0.26, d: 0.34, color: t.accent },
    { x: -4.2, y: -0.55, z: 0, w: 0.3, h: 0.5, d: 0.24, color: tire }, // tailwheel
  ];
}

// Starfang: an early-jet interceptor in the F-104 mold — a needle-nosed
// pencil fuselage, side intakes with shock cones, tiny razor mid-wings with
// tip tanks, a T-tail, and rocket pods on the stub pylons. No propeller;
// PlaneVisual gives jet types an afterburner plume instead (CLASS_JETPIPES).
function starfangBoxes(t) {
  return [
    // Pencil fuselage: long center tube tapering both ways, shaded belly.
    { x: 0.6, y: 0, z: 0, w: 7.6, h: 1.15, d: 1.15, color: t.body },
    { x: -3.9, y: 0.02, z: 0, w: 1.8, h: 1.0, d: 1.0, color: t.body },
    { x: 0.4, y: -0.52, z: 0, w: 7.0, h: 0.22, d: 0.95, color: shade(t.body, 0.72) },
    // Needle nose: a true smooth cone from the fuselage break to the tip,
    // with a short dark radome cap and the steel pitot boom off the point.
    { cone: true, x: 5.55, y: 0.02, z: 0, len: 2.5, r0: 0.57, r1: 0.16, seg: 12, color: t.body },
    { cone: true, x: 7.05, y: 0.05, z: 0, len: 0.55, r0: 0.16, r1: 0.02, seg: 12, color: dark },
    { x: 7.75, y: 0.06, z: 0, w: 0.9, h: 0.09, d: 0.09, color: steel }, // pitot boom
    // Vulcan cannon port on the nose's port cheek (the six-barrel gatling
    // lives inside; only the gas port and a soot streak show).
    { x: 4.35, y: -0.18, z: -0.5, w: 0.8, h: 0.22, d: 0.16, color: gunmetal },
    { x: 3.7, y: -0.18, z: -0.5, w: 0.6, h: 0.14, d: 0.12, color: 0x2a2620 },
    // Low-profile canopy faired into the spine, well forward.
    { x: 3.15, y: 0.62, z: 0, w: 0.24, h: 0.4, d: 0.8, color: t.body }, // windscreen frame
    { x: 2.45, y: 0.68, z: 0, w: 1.3, h: 0.5, d: 0.78, color: glass },
    { x: 2.45, y: 0.96, z: 0, w: 1.0, h: 0.16, d: 0.6, color: glassDeep },
    { x: 1.5, y: 0.6, z: 0, w: 0.7, h: 0.36, d: 0.74, color: t.body }, // headrest fairing
    { x: 0.0, y: 0.5, z: 0, w: 2.6, h: 0.22, d: 0.6, color: t.body }, // spine
    { x: -2.6, y: 0.44, z: 0, w: 2.6, h: 0.18, d: 0.5, color: t.body },
    ...mirrorZ([
      // Side intakes with half-cone centerbodies, flanking the cockpit.
      { x: 1.7, y: -0.05, z: 0.72, w: 1.7, h: 0.72, d: 0.5, color: shade(t.body, 0.85) },
      { x: 2.55, y: -0.05, z: 0.72, w: 0.3, h: 0.6, d: 0.4, color: dark }, // intake lip
      { x: 2.75, y: -0.05, z: 0.72, w: 0.5, h: 0.3, d: 0.2, color: steel }, // shock cone
      // Razor mid-wing: one thin slab each side, slight taper via a cap.
      { x: -0.7, y: -0.15, z: 1.95, w: 2.3, h: 0.16, d: 2.9, color: t.color },
      { x: -0.85, y: -0.15, z: 3.15, w: 1.8, h: 0.16, d: 0.9, color: t.color },
      { x: -1.55, y: -0.15, z: 2.2, w: 0.5, h: 0.14, d: 2.2, color: shade(t.color, 0.78) }, // aileron strip
      { x: -0.5, y: -0.05, z: 2.1, w: 1.1, h: 0.1, d: 1.1, color: t.accent }, // roundel
      // Tip tanks: smooth cigar pods capping the very end of the wing — a
      // cylinder body with cone nose and tail, centred on the tip edge.
      { cone: true, x: -0.5, y: -0.15, z: 3.62, len: 2.6, r0: 0.26, r1: 0.26, seg: 10, color: t.accent },
      { cone: true, x: 1.15, y: -0.15, z: 3.62, len: 0.7, r0: 0.26, r1: 0.03, seg: 10, color: steel },
      { cone: true, x: -2.15, y: -0.15, z: 3.62, len: 0.7, r0: 0.03, r1: 0.26, seg: 10, color: shade(t.accent, 0.8) },
      // Rocket pods slung under the inner wing: stubby seven-tube cans.
      { x: -0.4, y: -0.5, z: 1.5, w: 1.7, h: 0.44, d: 0.44, color: gunmetal },
      { x: 0.5, y: -0.5, z: 1.5, w: 0.24, h: 0.36, d: 0.36, color: 0x101114 }, // open tubes
    ]),
    { x: -0.5, y: 0.18, z: 3.62, w: 0.2, h: 0.14, d: 0.2, color: navGreen },
    { x: -0.5, y: 0.18, z: -3.62, w: 0.2, h: 0.14, d: 0.2, color: navRed },
    // Tall fin with a T-tail plane riding its top, plus a ventral strake.
    { x: -3.6, y: 0.6, z: 0, w: 1.3, h: 0.5, d: 0.22, color: t.body }, // fin fillet
    { x: -4.15, y: 1.5, z: 0, w: 1.1, h: 1.9, d: 0.24, color: t.body },
    { x: -4.7, y: 1.6, z: 0, w: 0.5, h: 1.9, d: 0.22, color: t.color }, // rudder
    { x: -4.7, y: 1.05, z: 0, w: 0.46, h: 0.34, d: 0.28, color: t.accent }, // rudder stripe
    { x: -4.3, y: 2.5, z: 0, w: 1.5, h: 0.18, d: 3.2, color: t.color }, // T-tailplane
    { x: -4.95, y: 2.5, z: 0, w: 0.34, h: 0.15, d: 2.9, color: shade(t.color, 0.78) },
    ...mirrorZ([{ x: -4.35, y: 2.5, z: 1.45, w: 1.2, h: 0.2, d: 0.34, color: t.accent }]),
    { x: -4.0, y: -0.62, z: 0, w: 1.0, h: 0.5, d: 0.16, color: t.body }, // ventral strake
    // Afterburner can: dark nozzle ring around the tailpipe.
    { x: -4.95, y: 0.02, z: 0, w: 0.6, h: 0.82, d: 0.82, color: gunmetal },
    { x: -5.25, y: 0.02, z: 0, w: 0.3, h: 0.62, d: 0.62, color: 0x101114 },
  ];
}

// --- the early-jets era hangar (docs/early-jets-era.md) --------------------
// Three more jet silhouettes for the era roster. Same rules as everything
// above: chunky boxes, nose +X, cones only where the real airplane was
// genuinely round. Era rosters name these via each type's `model` key and
// applyEraModels() swaps the per-index tables at boot.

// Balalaika: the MiG-21 — a stubby pencil with the intake IN THE NOSE around
// a shock cone, a big delta wing, a swept fin, and two missile rails. Reads
// instantly against the Starfang's T-tail and tip tanks.
function balalaikaBoxes(t) {
  return [
    // Fuselage: shorter and a hair fatter than the Starfang's pencil.
    { x: 0.2, y: 0, z: 0, w: 6.6, h: 1.2, d: 1.2, color: t.body },
    { x: -3.7, y: 0.04, z: 0, w: 1.6, h: 1.0, d: 1.0, color: t.body },
    { x: 0.2, y: -0.52, z: 0, w: 6.2, h: 0.22, d: 0.95, color: shade(t.body, 0.72) },
    // Nose intake: a ring lip with the steel shock cone poking through.
    { cone: true, x: 3.9, y: 0, z: 0, len: 1.4, r0: 0.6, r1: 0.5, seg: 12, color: t.body },
    { x: 4.68, y: 0, z: 0, w: 0.22, h: 1.0, d: 1.0, color: dark }, // intake lip
    { cone: true, x: 4.85, y: 0, z: 0, len: 1.0, r0: 0.34, r1: 0.04, seg: 10, color: steel }, // shock cone
    { x: 5.6, y: 0.3, z: 0, w: 0.8, h: 0.08, d: 0.08, color: steel }, // pitot boom, high like the real one
    // Canopy faired into a spine that runs all the way to the fin.
    { x: 2.6, y: 0.62, z: 0, w: 0.24, h: 0.38, d: 0.78, color: t.body },
    { x: 1.95, y: 0.68, z: 0, w: 1.2, h: 0.48, d: 0.76, color: glass },
    { x: 1.95, y: 0.95, z: 0, w: 0.9, h: 0.15, d: 0.58, color: glassDeep },
    { x: -0.3, y: 0.58, z: 0, w: 3.4, h: 0.3, d: 0.62, color: t.body }, // dorsal spine
    // The lone heavy cannon on the port cheek.
    { x: 3.0, y: -0.32, z: -0.5, w: 0.8, h: 0.2, d: 0.16, color: gunmetal },
    ...mirrorZ([
      // Delta wing: three stacked slabs whose chord shrinks outboard and
      // whose leading edge steps back — the triangle reads from every angle.
      { x: -0.9, y: -0.18, z: 1.05, w: 3.4, h: 0.16, d: 1.6, color: t.color },
      { x: -1.35, y: -0.18, z: 2.35, w: 2.4, h: 0.15, d: 1.2, color: t.color },
      { x: -1.8, y: -0.18, z: 3.3, w: 1.4, h: 0.14, d: 0.85, color: t.color },
      { x: -2.5, y: -0.18, z: 1.9, w: 0.45, h: 0.13, d: 1.7, color: shade(t.color, 0.78) }, // elevon strip
      { x: -0.7, y: -0.06, z: 1.6, w: 1.1, h: 0.1, d: 1.1, color: t.accent }, // roundel
      // Missile rail + slim round on each wing.
      { x: -0.4, y: -0.42, z: 2.5, w: 1.5, h: 0.14, d: 0.14, color: gunmetal },
      { cone: true, x: -0.2, y: -0.58, z: 2.5, len: 1.7, r0: 0.13, r1: 0.13, seg: 8, color: steel },
      { cone: true, x: 0.95, y: -0.58, z: 2.5, len: 0.5, r0: 0.13, r1: 0.02, seg: 8, color: t.accent },
    ]),
    { x: -1.8, y: -0.1, z: 3.75, w: 0.2, h: 0.14, d: 0.2, color: navGreen },
    { x: -1.8, y: -0.1, z: -3.75, w: 0.2, h: 0.14, d: 0.2, color: navRed },
    // Swept fin (mid-set tailplane, no T): the fillet fakes the sweep.
    { x: -3.3, y: 0.7, z: 0, w: 1.7, h: 0.5, d: 0.22, color: t.body },
    { x: -4.0, y: 1.4, z: 0, w: 1.2, h: 1.5, d: 0.22, color: t.body },
    { x: -4.55, y: 1.35, z: 0, w: 0.45, h: 1.5, d: 0.2, color: t.color }, // rudder
    { x: -4.5, y: 0.95, z: 0, w: 0.42, h: 0.3, d: 0.26, color: t.accent }, // rudder stripe
    { x: -4.2, y: 0.15, z: 0, w: 1.3, h: 0.16, d: 3.0, color: t.color }, // mid tailplane
    { x: -3.9, y: -0.6, z: 0, w: 0.9, h: 0.45, d: 0.14, color: t.body }, // ventral fin
    // Afterburner can.
    { x: -4.6, y: 0.02, z: 0, w: 0.55, h: 0.8, d: 0.8, color: gunmetal },
    { x: -4.88, y: 0.02, z: 0, w: 0.28, h: 0.6, d: 0.6, color: 0x101114 },
  ];
}

// Rhino: the F-4 Phantom — a big, hunched twin-seat missile truck. Twin
// intakes, a low wing with UPTURNED tips, the anhedral (drooped) tailplane,
// four belly missiles and the strapped-on gun pod. Nothing else in the sky
// has this much airplane per airplane.
function rhinoBoxes(t) {
  return [
    // Fuselage: broad and deep, with the hunched spine of the real thing.
    { x: 0.9, y: 0, z: 0, w: 7.4, h: 1.5, d: 1.7, color: t.body },
    { x: -3.6, y: 0.1, z: 0, w: 2.2, h: 1.2, d: 1.4, color: t.body },
    { x: 0.9, y: -0.7, z: 0, w: 7.0, h: 0.26, d: 1.4, color: shade(t.body, 0.72) },
    // Radome: blunter than the Starfang's needle.
    { cone: true, x: 5.35, y: 0.05, z: 0, len: 2.1, r0: 0.72, r1: 0.14, seg: 12, color: t.body },
    { cone: true, x: 6.55, y: 0.08, z: 0, len: 0.5, r0: 0.14, r1: 0.03, seg: 10, color: dark },
    // Tandem cockpit: two glass segments under one long framed canopy.
    { x: 3.4, y: 0.88, z: 0, w: 0.26, h: 0.44, d: 0.9, color: t.body },
    { x: 2.7, y: 0.95, z: 0, w: 1.2, h: 0.55, d: 0.88, color: glass },
    { x: 1.9, y: 0.9, z: 0, w: 0.28, h: 0.44, d: 0.9, color: t.body }, // center frame
    { x: 1.2, y: 0.95, z: 0, w: 1.2, h: 0.55, d: 0.88, color: glass },
    { x: 0.2, y: 0.8, z: 0, w: 0.9, h: 0.4, d: 0.84, color: t.body }, // aft fairing
    { x: -1.4, y: 0.68, z: 0, w: 2.4, h: 0.3, d: 0.9, color: t.body }, // spine hump
    ...mirrorZ([
      // Big cheek intakes with splitter plates.
      { x: 2.3, y: 0.05, z: 1.05, w: 2.2, h: 1.1, d: 0.55, color: shade(t.body, 0.85) },
      { x: 3.35, y: 0.05, z: 1.05, w: 0.3, h: 0.95, d: 0.45, color: dark },
      { x: 3.35, y: 0.05, z: 0.78, w: 0.5, h: 1.0, d: 0.1, color: steel }, // splitter
      // Low wing with the famous upturned outer panel.
      { x: 0.3, y: -0.5, z: 1.9, w: 2.9, h: 0.2, d: 3.0, color: t.color },
      { x: 0.1, y: -0.28, z: 3.65, w: 2.2, h: 0.18, d: 0.85, color: t.color }, // upturned tip
      { x: -1.0, y: -0.5, z: 2.4, w: 0.5, h: 0.16, d: 2.0, color: shade(t.color, 0.78) }, // aileron
      { x: 0.55, y: -0.36, z: 2.2, w: 1.2, h: 0.1, d: 1.2, color: t.accent }, // roundel
      // Two semi-recessed missiles per side under the belly edge.
      { cone: true, x: 0.6, y: -0.86, z: 0.55, len: 2.0, r0: 0.15, r1: 0.15, seg: 8, color: steel },
      { cone: true, x: 1.95, y: -0.86, z: 0.55, len: 0.55, r0: 0.15, r1: 0.02, seg: 8, color: t.accent },
      { cone: true, x: -0.8, y: -0.92, z: 1.15, len: 2.0, r0: 0.15, r1: 0.15, seg: 8, color: steel },
      { cone: true, x: 0.55, y: -0.92, z: 1.15, len: 0.55, r0: 0.15, r1: 0.02, seg: 8, color: t.accent },
      // Twin engine trunks ending in side-by-side burner cans.
      { x: -4.55, y: -0.15, z: 0.45, w: 1.3, h: 0.85, d: 0.75, color: gunmetal },
      { x: -5.25, y: -0.15, z: 0.45, w: 0.3, h: 0.6, d: 0.55, color: 0x101114 },
      // Anhedral tailplane: drooped tips read as the Phantom.
      { x: -4.4, y: 0.25, z: 1.0, w: 1.5, h: 0.18, d: 1.4, color: t.color },
      { x: -4.5, y: -0.05, z: 2.1, w: 1.3, h: 0.16, d: 0.9, color: shade(t.color, 0.9) },
    ]),
    // The gun pod strapped on the centerline — the Vietnam lesson, visible.
    { cone: true, x: 3.3, y: -0.98, z: 0, len: 2.4, r0: 0.24, r1: 0.24, seg: 10, color: gunmetal },
    { cone: true, x: 4.75, y: -0.98, z: 0, len: 0.5, r0: 0.24, r1: 0.1, seg: 10, color: steel },
    { x: 0.3, y: 0.1, z: 3.95, w: 0.2, h: 0.14, d: 0.2, color: navGreen },
    { x: 0.3, y: 0.1, z: -3.95, w: 0.2, h: 0.14, d: 0.2, color: navRed },
    // One big broad fin.
    { x: -3.9, y: 0.9, z: 0, w: 1.8, h: 0.6, d: 0.24, color: t.body },
    { x: -4.5, y: 1.8, z: 0, w: 1.5, h: 1.7, d: 0.26, color: t.body },
    { x: -5.2, y: 1.75, z: 0, w: 0.5, h: 1.6, d: 0.22, color: t.color }, // rudder
    { x: -5.1, y: 1.2, z: 0, w: 0.46, h: 0.34, d: 0.3, color: t.accent },
  ];
}

// Arclight: the B-52 — a rangefinder error of an airplane. A long tube, a
// tall fin, high shoulder-mounted wings that step BACK as they go out (the
// sweep), four twin-engine pods slung underneath, and the quad-.50 tail
// sting. It fills the sky the way the Leviathan never did.
function arclightBoxes(t) {
  return [
    // Fuselage: a long pressure tube with a shaded belly.
    { x: -0.5, y: 0, z: 0, w: 17.0, h: 1.9, d: 1.9, color: t.body },
    { x: -0.5, y: -0.9, z: 0, w: 16.4, h: 0.3, d: 1.5, color: shade(t.body, 0.72) },
    { cone: true, x: 8.7, y: 0.1, z: 0, len: 2.6, r0: 0.95, r1: 0.3, seg: 12, color: t.body },
    { x: 10.1, y: 0.1, z: 0, w: 0.5, h: 0.55, d: 0.55, color: dark }, // nose cap
    // Flight-deck glazing: airliner-style strip windows, not a bomber glasshouse.
    { x: 7.2, y: 0.75, z: 0, w: 1.6, h: 0.5, d: 1.5, color: t.body },
    ...mirrorZ([{ x: 7.3, y: 0.8, z: 0.68, w: 1.2, h: 0.3, d: 0.12, color: glass }]),
    { x: 7.0, y: 1.0, z: 0, w: 0.9, h: 0.3, d: 1.1, color: glassDeep }, // eyebrow panes
    ...mirrorZ([
      // Shoulder wing in three swept steps (leading edge walks aft outboard).
      { x: 0.9, y: 0.75, z: 2.6, w: 3.6, h: 0.28, d: 4.4, color: t.color },
      { x: -0.4, y: 0.78, z: 6.6, w: 3.0, h: 0.24, d: 3.6, color: t.color },
      { x: -1.7, y: 0.8, z: 9.8, w: 2.4, h: 0.2, d: 2.8, color: t.color },
      { x: -2.9, y: 0.8, z: 7.0, w: 0.5, h: 0.18, d: 3.4, color: shade(t.color, 0.78) }, // flap line
      { x: 0.6, y: 0.95, z: 4.4, w: 1.3, h: 0.1, d: 1.3, color: t.accent }, // roundel
      { x: -2.6, y: 0.82, z: 11.1, w: 1.3, h: 0.22, d: 0.5, color: t.accent }, // tip cap
      // Four engine pods: two twin-pods per side slung on pylons.
      { x: 1.6, y: 0.2, z: 3.4, w: 0.24, h: 0.6, d: 0.2, color: t.body }, // pylon
      { cone: true, x: 1.9, y: -0.15, z: 3.25, len: 2.2, r0: 0.4, r1: 0.34, seg: 10, color: gunmetal },
      { cone: true, x: 1.9, y: -0.15, z: 3.62, len: 2.2, r0: 0.4, r1: 0.34, seg: 10, color: gunmetal },
      { x: 0.4, y: 0.25, z: 7.2, w: 0.24, h: 0.55, d: 0.2, color: t.body },
      { cone: true, x: 0.7, y: -0.1, z: 7.05, len: 2.1, r0: 0.38, r1: 0.32, seg: 10, color: gunmetal },
      { cone: true, x: 0.7, y: -0.1, z: 7.42, len: 2.1, r0: 0.38, r1: 0.32, seg: 10, color: gunmetal },
    ]),
    { x: -2.6, y: 0.95, z: 11.35, w: 0.24, h: 0.16, d: 0.24, color: navGreen },
    { x: -2.6, y: 0.95, z: -11.35, w: 0.24, h: 0.16, d: 0.24, color: navRed },
    // The tall broad fin and low-set tailplane.
    { x: -7.6, y: 1.4, z: 0, w: 2.2, h: 0.9, d: 0.26, color: t.body },
    { x: -8.3, y: 2.8, z: 0, w: 1.8, h: 2.4, d: 0.28, color: t.body },
    { x: -9.1, y: 2.7, z: 0, w: 0.55, h: 2.2, d: 0.24, color: t.color }, // rudder
    { x: -9.0, y: 1.7, z: 0, w: 0.5, h: 0.4, d: 0.32, color: t.accent },
    { x: -8.4, y: 0.3, z: 0, w: 2.0, h: 0.22, d: 7.6, color: t.color }, // tailplane
    ...mirrorZ([{ x: -8.5, y: 0.32, z: 3.6, w: 1.6, h: 0.26, d: 0.5, color: t.accent }]),
    // The tail gunner's quad-.50 station (the crewable mount rides on top).
    { x: -9.5, y: 0.5, z: 0, w: 1.0, h: 0.8, d: 1.0, color: t.body },
    { x: -9.9, y: 0.75, z: 0, w: 0.5, h: 0.35, d: 0.9, color: glassDeep },
  ];
}

// Per-model bundles for era rosters (applyEraModels): everything the visual
// tables say about one silhouette, keyed by the config's `model` name.
const ERA_MODELS = {
  starfang: {
    builder: starfangBoxes,
    props: [],
    jet: true,
    jetpipe: { pos: [-5.4, 0.02, 0] },
    trails: { nacelles: 0.9, tip: 4.6 },
    muzzles: [[4.8, -0.18, -0.5]],
    damage: null, // null = copy the WW2 index's anchors (set below)
    stripe: [{ x: -4.72, y: 1.62, z: 0, w: 0.52, h: 0.5, d: 0.26 }],
  },
  balalaika: {
    builder: balalaikaBoxes,
    props: [],
    jet: true,
    jetpipe: { pos: [-5.0, 0.02, 0] },
    trails: { nacelles: 0.8, tip: 3.8 },
    muzzles: [[3.5, -0.32, -0.5]],
    damage: {
      engine: { x: -4.2, y: 0.02, z: 0, r: 0.6 },
      wing: { x: -1.1, y: -0.18, tipZ: 3.8, chord: 1.4, thick: 0.2 },
      fuse: { x0: -3.0, x1: 3.2, y: 0.2, z: 0.6, h: 0.6 },
      canopy: { x: 1.95, y: 0.75, w: 1.2, d: 0.76 },
      tank: { x: 0.1, y: -0.56, len: 2.2 },
      tail: { x: -4.2, y: 0.15, spanZ: 1.5, finY: 1.6 },
    },
    stripe: [{ x: -4.5, y: 1.55, z: 0, w: 0.45, h: 0.5, d: 0.24 }],
  },
  rhino: {
    builder: rhinoBoxes,
    props: [],
    jet: true,
    jetpipe: { pos: [-5.3, -0.15, 0] },
    trails: { nacelles: 1.2, tip: 4.1 },
    muzzles: [[4.9, -0.98, 0]],
    damage: {
      engine: { x: -4.5, y: -0.15, z: 0.45, r: 0.7 },
      wing: { x: 0.2, y: -0.5, tipZ: 4.1, chord: 1.5, thick: 0.24 },
      fuse: { x0: -3.4, x1: 3.8, y: 0.35, z: 0.85, h: 0.9 },
      canopy: { x: 2.0, y: 1.0, w: 2.7, d: 0.9 },
      tank: { x: 0.6, y: -0.75, len: 3.0 },
      tail: { x: -4.6, y: 0.25, spanZ: 2.6, finY: 2.2 },
    },
    stripe: [{ x: -5.15, y: 1.8, z: 0, w: 0.5, h: 0.5, d: 0.28 }],
  },
  arclight: {
    builder: arclightBoxes,
    props: [],
    jet: true, // eight turbojets — no plume, but the SOUND is all jet
    jetpipe: null, // podded engines, no burner plume — the contrails carry it
    trails: { nacelles: 5.2, tip: 11.2 },
    muzzles: [
      [10.3, -0.2, 0.3],
      [10.3, -0.2, -0.3],
    ],
    turrets: [{ kind: "tail", pos: [-9.6, 0.6, 0], stowYaw: Math.PI, stowPitch: 0.1, barrelLen: 1.6 }],
    damage: {
      engine: { x: 1.9, y: -0.15, z: 3.4, r: 0.75 },
      wing: { x: 0, y: 0.78, tipZ: 11.0, chord: 2.6, thick: 0.5 },
      fuse: { x0: -7.0, x1: 7.0, y: 0.7, z: 1.0, h: 1.4 },
      canopy: { x: 7.2, y: 0.95, w: 1.6, d: 1.5 },
      tank: { x: -0.5, y: -1.0, len: 5.0 },
      tail: { x: -8.4, y: 0.5, spanZ: 3.8, finY: 3.4 },
    },
    stripe: [{ x: -9.05, y: 2.9, z: 0, w: 0.6, h: 0.6, d: 0.3 }],
  },
};

/**
 * Rewire the per-index visual tables for an era roster (docs/early-jets-era.md).
 * The classic tables are indexed by the AIR game's aircraft order; an era
 * server serves a different roster, so each type names its silhouette via a
 * `model` key and this swaps every table in place (imports keep working) at
 * boot, before any geometry is built. A roster with no model keys (the WW2
 * game) is left completely alone.
 */
export function applyEraModels(aircraftTypes) {
  if (!aircraftTypes?.some((t) => t?.model)) return;
  aircraftTypes.forEach((t, i) => {
    const m = ERA_MODELS[t?.model];
    if (!m) return;
    CLASS_BUILDERS[i] = m.builder;
    CLASS_PROPS[i] = m.props ?? [];
    if (m.jetpipe) CLASS_JETPIPES[i] = m.jetpipe;
    else delete CLASS_JETPIPES[i];
    if (m.jet) CLASS_JET.add(i);
    else CLASS_JET.delete(i);
    CLASS_TRAILS[i] = m.trails ?? CLASS_TRAILS[i];
    CLASS_MUZZLES[i] = m.muzzles ?? [];
    if (m.turrets) CLASS_TURRETS[i] = m.turrets;
    else delete CLASS_TURRETS[i];
    delete CLASS_BANNERS[i]; // no circus in an era roster
    if (m.damage) DAMAGE_ANCHORS[i] = m.damage;
    else if (i !== 5 && DAMAGE_ANCHORS[5]) DAMAGE_ANCHORS[i] = DAMAGE_ANCHORS[5]; // starfang keeps its own
    if (m.stripe) TAIL_STRIPE_ANCHORS[i] = m.stripe;
  });
}

// --- the flying billboard circus (adfleet.go) ------------------------------
// Bot-only, teamless craft: they ignore the team palette and fly a carnival
// livery instead — cream, circus gold, and poster red — so nobody reads them
// as a bandit at a glance. Their banners are separate canvas-textured meshes
// built by PlaneVisual (see _buildBanner), not part of the merged hull.

const circusCream = 0xefe2c0;
const circusGold = 0xf0c04a;
const circusRed = 0xc8452e;
const zeppelinSkin = 0xc9cdd4;

// Skywriter: a fabric-and-wire barnstormer — twin stacked wings, struts,
// open cockpit, fixed gear with a tailskid, and a tow hook for the banner.
function skywriterBoxes() {
  return [
    // Fuselage, tapering aft, with a shaded belly line.
    { x: 0.7, y: 0, z: 0, w: 4.2, h: 1.2, d: 1.2, color: circusCream },
    { x: -1.9, y: 0.05, z: 0, w: 1.6, h: 1.0, d: 1.0, color: circusCream },
    { x: -3.1, y: 0.1, z: 0, w: 1.2, h: 0.8, d: 0.8, color: circusCream },
    { x: 0.7, y: -0.55, z: 0, w: 3.8, h: 0.24, d: 0.95, color: shade(circusCream, 0.74) },
    // Radial cowl in poster red, lip and spinner.
    { x: 3.1, y: 0, z: 0, w: 1.3, h: 1.4, d: 1.5, color: circusRed },
    { x: 3.8, y: 0, z: 0, w: 0.28, h: 1.2, d: 1.3, color: gunmetal },
    { x: 4.0, y: 0, z: 0, w: 0.5, h: 0.55, d: 0.55, color: steel },
    ...mirrorZ([{ x: 2.3, y: -0.16, z: 0.68, w: 1.3, h: 0.24, d: 0.16, color: exhaustBrown }]),
    // Open cockpit: a windscreen, a padded coaming, a headrest fairing.
    { x: 1.0, y: 0.78, z: 0, w: 0.24, h: 0.44, d: 0.9, color: glass },
    { x: 0.3, y: 0.68, z: 0, w: 1.2, h: 0.16, d: 1.0, color: shade(circusRed, 0.8) },
    { x: -0.9, y: 0.62, z: 0, w: 1.2, h: 0.34, d: 0.6, color: circusCream },
    // Twin stacked wings — the whole point of a biplane — gold with red tips.
    { x: 0.9, y: 1.2, z: 0, w: 2.2, h: 0.26, d: 11.0, color: circusGold }, // upper
    { x: 0.5, y: -0.5, z: 0, w: 2.1, h: 0.26, d: 9.2, color: circusGold }, // lower
    ...mirrorZ([
      { x: 0.9, y: 1.2, z: 5.25, w: 2.2, h: 0.3, d: 0.55, color: circusRed }, // upper tips
      { x: 0.5, y: -0.5, z: 4.35, w: 2.1, h: 0.3, d: 0.5, color: circusRed }, // lower tips
      // Interplane struts and flying wires do the "wire-and-fabric" read.
      { x: 1.35, y: 0.35, z: 3.4, w: 0.14, h: 1.75, d: 0.14, color: dark },
      { x: 0.35, y: 0.35, z: 3.4, w: 0.14, h: 1.75, d: 0.14, color: dark },
      // Cabane struts from fuselage crown to the upper wing.
      { x: 1.2, y: 0.95, z: 0.5, w: 0.12, h: 0.6, d: 0.12, color: dark },
      { x: 0.4, y: 0.95, z: 0.5, w: 0.12, h: 0.6, d: 0.12, color: dark },
      // Fixed landing gear: leg + fat tire.
      { x: 1.6, y: -1.0, z: 0.85, w: 0.16, h: 0.85, d: 0.16, color: dark },
      { x: 1.6, y: -1.5, z: 0.85, w: 0.66, h: 0.66, d: 0.3, color: tire },
    ]),
    { x: 1.6, y: -1.15, z: 0, w: 0.14, h: 0.14, d: 1.9, color: dark }, // axle
    { x: -3.6, y: -0.45, z: 0, w: 0.5, h: 0.5, d: 0.12, color: dark }, // tailskid
    // Tail: gold planes, a red fin, and the banner tow hook.
    { x: -3.5, y: 0.3, z: 0, w: 1.2, h: 0.2, d: 3.8, color: circusGold },
    ...mirrorZ([{ x: -3.5, y: 0.3, z: 1.8, w: 1.2, h: 0.24, d: 0.4, color: circusRed }]),
    { x: -3.6, y: 0.85, z: 0, w: 1.0, h: 1.1, d: 0.2, color: circusRed },
    { x: -4.15, y: 0.7, z: 0, w: 0.4, h: 1.3, d: 0.16, color: shade(circusRed, 0.8) }, // rudder
    { x: -3.9, y: 0.05, z: 0, w: 0.35, h: 0.18, d: 0.18, color: steel }, // tow hook
  ];
}

// Marquee: the ad zeppelin — a long silver gasbag with a passenger gondola
// slung beneath and cruciform tail fins. No propellers: it drifts on quiet
// station-keeping engines the eye doesn't need. The flank banners are
// separate textured meshes (PlaneVisual._buildBanner) sized to the hull.
function marqueeBoxes() {
  return [
    // Hull: a fat center section with smooth tapered nose and tail cones.
    { cone: true, x: 0, y: 0, z: 0, len: 12, r0: 3.1, r1: 3.1, seg: 14, color: zeppelinSkin },
    { cone: true, x: 9.4, y: 0, z: 0, len: 7, r0: 3.1, r1: 1.0, seg: 14, color: zeppelinSkin },
    { x: 13.1, y: 0, z: 0, w: 0.7, h: 1.1, d: 1.1, color: circusRed }, // nose cap
    { cone: true, x: -9.4, y: 0, z: 0, len: 7, r0: 1.1, r1: 3.1, seg: 14, color: zeppelinSkin },
    // Dorsal and ventral ridge lines so the roll reads.
    { x: 0, y: 3.05, z: 0, w: 11.5, h: 0.18, d: 0.5, color: shade(zeppelinSkin, 0.8) },
    { x: 0, y: -3.05, z: 0, w: 11.5, h: 0.18, d: 0.5, color: shade(zeppelinSkin, 0.8) },
    // Cruciform tail: fins and rudder in poster red.
    { x: -11.2, y: 1.9, z: 0, w: 3.4, h: 2.6, d: 0.24, color: circusRed },
    { x: -11.2, y: -1.9, z: 0, w: 3.4, h: 2.6, d: 0.24, color: circusRed },
    { x: -11.2, y: 0, z: 1.9, w: 3.4, h: 0.24, d: 2.6, color: circusRed },
    { x: -11.2, y: 0, z: -1.9, w: 3.4, h: 0.24, d: 2.6, color: circusRed },
    { x: -13.0, y: 0.9, z: 0, w: 0.6, h: 1.6, d: 0.2, color: shade(circusRed, 0.8) }, // rudder
    // Gondola: a warm-lit cabin slung under the bow third.
    { x: 2.6, y: -3.5, z: 0, w: 4.6, h: 1.3, d: 1.5, color: circusRed },
    { x: 4.4, y: -3.3, z: 0, w: 1.0, h: 0.6, d: 1.3, color: glass }, // wheelhouse glass
    ...mirrorZ([{ x: 2.2, y: -3.4, z: 0.78, w: 2.6, h: 0.45, d: 0.1, color: glassDeep }]), // cabin windows
    { x: 2.6, y: -4.3, z: 0, w: 2.0, h: 0.3, d: 0.5, color: dark }, // landing bumper
    // Mooring lines' anchor points, a nod to the ground crew.
    { x: 8.0, y: -1.9, z: 0, w: 0.25, h: 0.7, d: 0.25, color: dark },
  ];
}

export const CLASS_BUILDERS = [sparrowhawkBoxes, thunderheadBoxes, ironcladBoxes, leviathanBoxes, swordfinBoxes, starfangBoxes, skywriterBoxes, marqueeBoxes];

// Ad-craft banner style per class (adfleet.go): "towed" hangs a cloth banner
// off the tail on a rope; "sides" paints one panel on each hull flank.
// Classes absent here fly no banner — this map is also how PlaneVisual knows
// a type belongs to the circus.
export const CLASS_BANNERS = {
  6: { kind: "towed" },
  7: { kind: "sides" },
};

// Per-class propeller spec: [x, z] positions of each prop.
const CLASS_PROPS = [
  [[4.5, 0]],
  [[5.4, 0]],
  [
    [2.95, 3.6],
    [2.95, -3.6],
  ],
  [
    [3.6, 2.9],
    [3.6, -2.9],
    [3.0, 5.7],
    [3.0, -5.7],
  ],
  [[5.0, 0]],
  [], // Starfang: a jet — no propeller (see CLASS_JETPIPES)
  [[4.15, 0]], // Skywriter: one honest radial up front
  [], // Marquee: a zeppelin drifts — no visible prop, no jet
];

// Jet types: tailpipe position for the afterburner plume PlaneVisual builds
// in place of a propeller. Flame length/opacity ride the throttle.
export const CLASS_JETPIPES = {
  5: { pos: [-5.4, 0.02, 0] },
};

// Which types are turbojets — the audio discriminator (audio.js swaps the
// piston drone for a thrust roar + turbine whine). This is NOT the same set
// as CLASS_JETPIPES: the Arclight's podded engines draw no burner plume but
// she's every bit a jet to the ear. Era rosters rewire this via each model's
// `jet` flag in applyEraModels.
export const CLASS_JET = new Set([5]);

// Contrail/streamer emit geometry per class (effects.js, roadmap #45): the
// body-frame ±Z offsets of the engine trails and the wingtip half-span the
// G-streamers snap off. Data only — main.js rotates these into the world.
export const CLASS_TRAILS = {
  0: { nacelles: 1.8, tip: 5.2 }, // Sparrowhawk
  1: { nacelles: 1.8, tip: 5.6 }, // Thunderhead
  2: { nacelles: 2.6, tip: 7.6 }, // Ironclad (twin engines)
  3: { nacelles: 2.6, tip: 10.4 }, // Leviathan
  4: { nacelles: 1.8, tip: 6.4 }, // Swordfin
  5: { nacelles: 0.9, tip: 4.6 }, // Starfang (jet: tight twin plume)
  6: { nacelles: 0.8, tip: 5.2 }, // Skywriter (the circus contrails too)
  7: { nacelles: 2.4, tip: 3.0 }, // Marquee (a lazy smudge off the gondola)
};

// Articulated defensive turrets per class (only the Leviathan carries any):
// crewable twin mounts modeled on a B-17's blister positions. Pivot positions
// mirror the `turrets` entries in shared/config.json — keep them in step so
// server rounds leave where the model's barrels point. Yaw is in the body
// frame (0 = over the nose, stowYaw parks the mount when unmanned).
export const CLASS_TURRETS = {
  3: [
    { kind: "tail", pos: [-7.8, 0.4, 0], stowYaw: Math.PI, stowPitch: 0.1, barrelLen: 1.5 },
    { kind: "top", pos: [-1.6, 1.7, 0], stowYaw: Math.PI, stowPitch: 0.12, barrelLen: 1.3 },
  ],
};

// Rotating part of a crew turret: the dorsal glass dome or the tail cradle,
// with a pitching twin-gun assembly nested inside (built by PlaneVisual).
function turretDomeBoxes(kind) {
  if (kind === "top") {
    return [
      { x: 0, y: -0.15, z: 0, w: 0.95, h: 0.5, d: 0.95, color: glassDeep },
      { x: 0.28, y: 0.08, z: 0, w: 0.5, h: 0.24, d: 0.7, color: steel }, // gun slot fairing
    ];
  }
  // Tail cradle: a small armored yoke under the glasshouse.
  return [{ x: 0.1, y: -0.12, z: 0, w: 0.5, h: 0.4, d: 0.6, color: steel }];
}

function turretGunBoxes(kind, barrelLen) {
  const y = kind === "top" ? 0.12 : 0.05;
  return [
    ...mirrorZ([
      { x: barrelLen * 0.45, y, z: 0.24, w: barrelLen, h: 0.13, d: 0.13, color: gunmetal },
      { x: barrelLen * 0.95, y, z: 0.24, w: 0.4, h: 0.09, d: 0.09, color: steel }, // muzzle tips
    ]),
    { x: -0.1, y, z: 0, w: 0.45, h: 0.3, d: 0.75, color: gunmetal }, // receiver block
  ];
}

// Per-class gun muzzle offsets [x, y, z] — one flash per barrel, placed at
// the model's barrel tips. The caliber doctrine sets the count: fifties fly
// quads (a pair per wing, a foot apart), 20 mm cannons fly the classic
// pair, and the heavies are singles — the Ironclad's centerline chin
// cannon, the Starfang's cheek gatling.
export const CLASS_MUZZLES = [
  [ // Sparrowhawk quad fifties
    [2.5, -0.3, 2.95], [2.5, -0.3, 3.25],
    [2.5, -0.3, -2.95], [2.5, -0.3, -3.25],
  ],
  [ // Thunderhead 20 mm pair
    [3.8, -0.25, 3.2],
    [3.8, -0.25, -3.2],
  ],
  [ // Ironclad: the lone 30 mm chin cannon on the centerline
    [5.1, -0.62, 0],
  ],
  [ // Leviathan chin pair
    [5.9, -0.3, 0.9],
    [5.9, -0.3, -0.9],
  ],
  [ // Swordfin quad fifties on the outer panels
    [2.3, -0.42, 3.25], [2.3, -0.42, 3.55],
    [2.3, -0.42, -3.25], [2.3, -0.42, -3.55],
  ],
  [ // Starfang: the lone gatling port
    [4.8, -0.18, -0.5],
  ],
  [], // Skywriter: unarmed — the circus never flashes a muzzle
  [], // Marquee: likewise
];

// --- visible battle damage --------------------------------------------------
// The component damage model (engine, wings, fuel, pilot, airframe HP) is
// otherwise read only through smoke/fuel trails. These overlays make a hit
// legible on the airframe itself: a scorched cowl, a shredded wingtip, flak
// holes punched along the fuselage — still chunky boxes, matching the build.

const charBlack = 0x14110e; // soot-blackened panel
const sootEdge = 0x35302a; // singed metal around a burn
const blastHole = 0x070708; // punched-through hole
const tornMetal = 0x2a2d33; // ragged exposed structure / shredded skin
const bareEdge = 0x595e66; // bright torn-aluminium lip
const oilSoot = 0x1a1714; // oil/soot streak blown back from the engine
const fuelStain = 0x15191c; // dark wet fuel weeping back along the belly
const shatterGlass = 0x222a2e; // blown-out, darkened canopy glass
const canopyGlint = 0xaec2cd; // pale glint along a fresh crack in the glass

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerpN = (a, b, t) => a + (b - a) * t;

// Tiny deterministic PRNG so a given plane + damage bucket always shreds the
// same way — no flicker between frames or between screenshot runs.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A stable 32-bit seed for one bullet hole (roadmap #8): a deterministic mix of
// the entity id, the component it's on, and the hole index — so hole #i on a
// given plane's given component lands in the same spot on every client and in
// the killcam, and new holes accrete beside the old ones as damage climbs.
function holeSeed(id, comp, i) {
  let h = ((id >>> 0) ^ 0x9e3779b1) | 0;
  h = Math.imul(h ^ (comp + 1), 0x85ebca77);
  h = Math.imul(h ^ (i + 1), 0xc2b2ae3d);
  return (h ^ (h >>> 15)) >>> 0;
}

// Per-class anchors for battle damage, in model space (nose +X, up +Y, right
// wing +Z), eyeballed from the silhouettes above. `engine` is the cowl or the
// starboard (out-board) nacelle that takes the scorch on multi-engine types.
const DAMAGE_ANCHORS = [
  { // 0 Sparrowhawk
    engine: { x: 3.5, y: 0, z: 0, r: 0.82 },
    wing: { x: 0.5, y: -0.32, tipZ: 5.3, chord: 1.15, thick: 0.34 },
    fuse: { x0: -1.6, x1: 1.8, y: 0.3, z: 0.62, h: 0.7 },
    canopy: { x: 0.85, y: 1.0, w: 1.3, d: 0.95 },
    tank: { x: 0.4, y: -0.62, len: 2.2 },
    tail: { x: -4.0, y: 0.35, spanZ: 2.2, finY: 1.5 },
  },
  { // 1 Thunderhead
    engine: { x: 4.5, y: -0.02, z: 0, r: 0.76 },
    wing: { x: 0.6, y: -0.3, tipZ: 5.5, chord: 0.95, thick: 0.3 },
    fuse: { x0: -1.8, x1: 2.4, y: 0.28, z: 0.6, h: 0.65 },
    canopy: { x: -0.35, y: 1.0, w: 1.7, d: 1.0 },
    tank: { x: 0.6, y: -0.58, len: 2.6 },
    tail: { x: -4.5, y: 0.32, spanZ: 1.95, finY: 1.3 },
  },
  { // 2 Ironclad (starboard nacelle)
    engine: { x: 2.3, y: -0.32, z: 3.6, r: 0.78 },
    wing: { x: 0.15, y: -0.44, tipZ: 6.9, chord: 1.4, thick: 0.4 },
    fuse: { x0: -2.6, x1: 2.6, y: 0.5, z: 0.86, h: 1.0 },
    canopy: { x: 0.9, y: 1.12, w: 1.7, d: 1.2 },
    tank: { x: 0.2, y: -0.94, len: 3.0 },
    tail: { x: -4.55, y: 0.45, spanZ: 2.9, finY: 1.6 },
  },
  { // 3 Leviathan (starboard outboard nacelle)
    engine: { x: 2.4, y: -0.38, z: 5.7, r: 0.78 },
    wing: { x: 0.5, y: -0.4, tipZ: 9.5, chord: 1.7, thick: 0.46 },
    fuse: { x0: -4.0, x1: 3.4, y: 0.6, z: 1.0, h: 1.3 },
    canopy: { x: 2.45, y: 1.32, w: 1.8, d: 1.4 },
    tank: { x: 0.2, y: -1.1, len: 4.0 },
    tail: { x: -6.3, y: 0.55, spanZ: 3.7, finY: 2.6 },
  },
  { // 4 Swordfin
    engine: { x: 3.85, y: 0, z: 0, r: 0.95 },
    wing: { x: 0.55, y: -0.36, tipZ: 5.6, chord: 1.3, thick: 0.38 },
    fuse: { x0: -2.4, x1: 2.4, y: 0.42, z: 0.84, h: 0.95 },
    canopy: { x: 1.0, y: 1.05, w: 3.0, d: 1.2 },
    tank: { x: 0.4, y: -0.86, len: 2.6 },
    tail: { x: -4.4, y: 0.5, spanZ: 2.3, finY: 1.9 },
  },
  { // 5 Starfang (the turbojet burns at the tailpipe, not a cowl; T-tail)
    engine: { x: -4.6, y: 0.02, z: 0, r: 0.6 },
    wing: { x: -0.7, y: -0.15, tipZ: 3.7, chord: 1.15, thick: 0.2 },
    fuse: { x0: -3.2, x1: 3.6, y: 0.2, z: 0.58, h: 0.6 },
    canopy: { x: 2.45, y: 0.75, w: 1.3, d: 0.78 },
    tank: { x: 0.2, y: -0.56, len: 2.4 },
    tail: { x: -4.3, y: 2.5, spanZ: 1.6, finY: 2.6 },
  },
  { // 6 Skywriter (fabric burns fast; scorch on the red cowl)
    engine: { x: 3.1, y: 0, z: 0, r: 0.75 },
    wing: { x: 0.7, y: 0.35, tipZ: 5.0, chord: 1.05, thick: 0.28 },
    fuse: { x0: -1.6, x1: 1.6, y: 0.25, z: 0.55, h: 0.6 },
    canopy: { x: 0.9, y: 0.85, w: 0.6, d: 0.9 },
    tank: { x: 0.5, y: -0.55, len: 2.0 },
  },
  { // 7 Marquee (hits pock the gasbag; the "engine" is the gondola)
    engine: { x: 2.6, y: -3.4, z: 0, r: 0.9 },
    wing: { x: 0, y: 0.4, tipZ: 3.0, chord: 4.0, thick: 0.6 },
    fuse: { x0: -8.0, x1: 8.0, y: 0.8, z: 2.9, h: 2.4 },
    canopy: { x: 4.4, y: -3.3, w: 1.0, d: 1.3 },
    tank: { x: -1.0, y: -2.8, len: 5.0 },
  },
];

// Advanced damage: a component this far gone loses its whole section — the
// outer wing shears off, the canopy is blown clean away, the empennage
// lets go and the wreck tumbles nose-heavy.
const WING_LOSE = 0.18; // wing health at/below which the outer panel tears off
const CANOPY_LOSE = 0.16; // pilot health at/below which the glasshouse goes
const TAIL_LOSE = 0.18; // tail health at/below which the empennage tears away
const WING_KEEP = 0.58; // fraction of the half-span that stays attached

// Distance from the centerline where a torn wing shears, per class.
function wingBreakAt(type) {
  return (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).wing.tipZ * WING_KEEP;
}

// Build the overlay boxes (scorch, holes, torn metal) for a damage state.
// d = { engine, wingL, wingR, hp, fuel, pilot, tail } in 0..1 (1 = pristine; missing =
// pristine). entityId seeds the per-plane bullet-hole scatter (roadmap #8) so it
// is deterministic across clients. Returns [] for an essentially undamaged plane.
function damageBoxes(type, d, entityId = 0) {
  const a = DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0];
  const boxes = [];
  const rnd = mulberry32(0x9e3779b1 ^ Math.imul(type + 1, 0x85ebca77));
  const jit = (s) => (rnd() - 0.5) * s;

  // Engine: blackened, soot-streaked cowl / nacelle.
  const eng = clamp01((0.78 - (d.engine ?? 1)) / 0.78);
  if (eng > 0.03) {
    const e = a.engine;
    const k = 0.4 + eng * 0.6;
    boxes.push({ x: e.x + 0.08, y: e.y + e.r * 0.45, z: e.z, w: 1.0, h: e.r * k, d: e.r * 1.75, color: charBlack });
    boxes.push({ x: e.x - 0.15, y: e.y, z: e.z + e.r * 0.85, w: 1.05, h: e.r * 1.3, d: 0.16, color: sootEdge });
    boxes.push({ x: e.x - 0.15, y: e.y, z: e.z - e.r * 0.85, w: 1.05, h: e.r * 1.3, d: 0.16, color: sootEdge });
    const streaks = 2 + Math.round(eng * 2);
    for (let i = 0; i < streaks; i++) {
      const len = 1.4 + rnd() * 2.4 * (0.6 + eng);
      boxes.push({ x: e.x - 0.8 - len / 2, y: e.y + e.r * 0.5 + jit(0.18), z: e.z + jit(e.r * 1.1), w: len, h: 0.1, d: 0.16 + rnd() * 0.12, color: oilSoot });
    }
  }

  // Wings: shredded, blackened tip on whichever side was hit — and a ragged
  // stump at the shear line once the outer panel has been torn clean away.
  for (const [side, h] of [[1, d.wingR ?? 1], [-1, d.wingL ?? 1]]) {
    const sev = clamp01((0.7 - h) / 0.7);
    if (sev <= 0.03) continue;
    const w = a.wing;
    if (h <= WING_LOSE) {
      // The outer wing is gone (clipped from the body): cap the break with a
      // charred, ragged stump and a glint of exposed spar.
      const cut = wingBreakAt(type);
      boxes.push({ x: w.x, y: w.y, z: side * cut, w: w.chord * 1.7, h: w.thick * 1.3, d: 0.4, color: charBlack });
      boxes.push({ x: w.x - w.chord * 0.2, y: w.y, z: side * (cut - 0.18), w: w.chord * 0.55, h: w.thick * 0.6, d: 0.3, color: bareEdge });
      for (let i = 0; i < 5; i++) {
        boxes.push({
          x: w.x + jit(w.chord * 1.3),
          y: w.y + jit(w.thick),
          z: side * cut + side * (rnd() * 0.45 - 0.1) + jit(0.25),
          w: 0.25 + rnd() * w.chord * 0.6,
          h: w.thick * (0.5 + rnd()),
          d: 0.16 + rnd() * 0.35,
          color: i % 2 ? tornMetal : charBlack,
        });
      }
      continue;
    }
    const z0 = side * w.tipZ;
    const span = w.tipZ * (0.16 + sev * 0.4);
    // Charred outer panel creeping inboard from the tip (top + underside).
    boxes.push({ x: w.x, y: w.y + w.thick * 0.55, z: side * (w.tipZ - span * 0.5), w: w.chord * 1.9, h: w.thick * 0.55, d: span, color: charBlack });
    boxes.push({ x: w.x, y: w.y - w.thick * 0.55, z: side * (w.tipZ - span * 0.5), w: w.chord * 1.7, h: w.thick * 0.45, d: span, color: sootEdge });
    // Ragged teeth where the tip was blown off.
    const teeth = 3 + Math.round(sev * 4);
    for (let i = 0; i < teeth; i++) {
      const t = i / teeth;
      boxes.push({
        x: w.x + jit(w.chord * 1.3),
        y: w.y + jit(w.thick),
        z: z0 - side * t * span * 0.7 + jit(0.3),
        w: 0.3 + rnd() * w.chord * 0.7,
        h: w.thick * (0.5 + rnd()),
        d: 0.18 + rnd() * 0.4,
        color: i % 2 ? tornMetal : charBlack,
      });
    }
    // A blown-through hole mid-outer wing once it is badly hit.
    if (sev > 0.45) {
      const hz = side * w.tipZ * (0.5 + jit(0.08));
      boxes.push({ x: w.x + jit(0.4), y: w.y, z: hz, w: w.chord * 0.9, h: w.thick * 1.5, d: 0.5 + sev * 0.6, color: blastHole });
      boxes.push({ x: w.x + jit(0.4), y: w.y + w.thick * 0.45, z: hz, w: w.chord * 1.25, h: 0.1, d: 0.9, color: bareEdge });
    }
  }

  // Tail: chewed-up elevators and a shot-through fin — and once the whole
  // empennage has torn away, a ragged charred stump at the break.
  const tailv = clamp01((0.7 - (d.tail ?? 1)) / 0.7);
  if (tailv > 0.03) {
    const tl = a.tail;
    if ((d.tail ?? 1) <= TAIL_LOSE) {
      // The empennage is gone (clipped from the body): a torn, blackened
      // break where the boom ends, spar stubs poking aft.
      const cut = tl.x + 0.55;
      boxes.push({ x: cut, y: tl.y * 0.6, z: 0, w: 0.5, h: 0.9, d: 0.9, color: charBlack });
      boxes.push({ x: cut - 0.2, y: tl.y * 0.6 + 0.15, z: 0, w: 0.35, h: 0.4, d: 0.5, color: bareEdge });
      for (let i = 0; i < 4; i++) {
        boxes.push({
          x: cut - 0.15 - rnd() * 0.4,
          y: tl.y * 0.6 + jit(0.6),
          z: jit(0.7),
          w: 0.2 + rnd() * 0.5,
          h: 0.1 + rnd() * 0.2,
          d: 0.1 + rnd() * 0.2,
          color: i % 2 ? tornMetal : charBlack,
        });
      }
    } else {
      // Charred elevator tips creeping inboard, soot up the fin.
      const span = tl.spanZ * (0.2 + tailv * 0.45);
      for (const side of [1, -1]) {
        boxes.push({ x: tl.x, y: tl.y + 0.16, z: side * (tl.spanZ - span * 0.5), w: 1.1, h: 0.14, d: span, color: charBlack });
      }
      boxes.push({ x: tl.x - 0.3, y: (tl.y + tl.finY) * 0.5, z: 0, w: 0.5, h: (tl.finY - tl.y) * (0.3 + tailv * 0.5), d: 0.3, color: sootEdge });
      // Ragged bites out of the elevator trailing edge once it's badly hit.
      if (tailv > 0.45) {
        const teeth = 2 + Math.round(tailv * 3);
        for (let i = 0; i < teeth; i++) {
          const sgn = i % 2 ? 1 : -1;
          boxes.push({
            x: tl.x - 0.4 + jit(0.3),
            y: tl.y + jit(0.12),
            z: sgn * (0.5 + rnd() * (tl.spanZ - 0.5)),
            w: 0.25 + rnd() * 0.4,
            h: 0.3,
            d: 0.2 + rnd() * 0.35,
            color: i % 2 ? blastHole : tornMetal,
          });
        }
      }
    }
  }

  // Airframe HP: scorch patches and flak holes scattered down the fuselage.
  const hpv = clamp01((0.7 - (d.hp ?? 1)) / 0.7);
  if (hpv > 0.03) {
    const f = a.fuse;
    const n = 2 + Math.round(hpv * 5);
    for (let i = 0; i < n; i++) {
      const x = lerpN(f.x0, f.x1, rnd());
      const side = rnd() > 0.5 ? 1 : -1;
      const onTop = rnd() > 0.6;
      const y = onTop ? f.y + f.h * 0.55 : f.y + jit(f.h);
      const z = onTop ? jit(f.z * 0.8) : side * (f.z + 0.03);
      const s = 0.35 + rnd() * 0.6;
      boxes.push({
        x, y, z,
        w: s,
        h: onTop ? 0.12 : s * 0.9,
        d: onTop ? s : 0.12,
        color: i % 3 ? charBlack : sootEdge,
      });
      if (hpv > 0.4 && rnd() > 0.55) {
        boxes.push({
          x: x + jit(0.12), y, z: z + (onTop ? 0 : side * 0.04),
          w: s * 0.55,
          h: onTop ? 0.14 : s * 0.5,
          d: onTop ? s * 0.55 : 0.14,
          color: blastHole,
        });
      }
    }
  }

  // Fuel: a dark wet streak weeping back along the belly from the punctured tank.
  const fuelv = clamp01((0.85 - (d.fuel ?? 1)) / 0.85);
  if (fuelv > 0.05) {
    const tk = a.tank;
    const n = 2 + Math.round(fuelv * 3);
    for (let i = 0; i < n; i++) {
      const back = (i / n) * tk.len;
      boxes.push({ x: tk.x - back, y: tk.y - 0.03, z: jit(0.5), w: 0.5 + rnd() * 0.6, h: 0.08, d: 0.16 + rnd() * 0.22, color: fuelStain });
    }
  }

  // Pilot: a shattered canopy where the cockpit took a round — or, once the
  // glasshouse has been blown clean off, a torn, empty coaming.
  const pilotv = clamp01((0.6 - (d.pilot ?? 1)) / 0.6);
  if (pilotv > 0.05) {
    const cp = a.canopy;
    if ((d.pilot ?? 1) <= CANOPY_LOSE) {
      // Glass blown clean off: torn frame rails fore and aft of an open, dark
      // cockpit, ringed with jagged shattered-glass remnants still in the sill.
      const rim = cp.d * 0.5;
      boxes.push({ x: cp.x + cp.w * 0.46, y: cp.y - 0.02, z: 0, w: 0.2, h: 0.36, d: cp.d * 0.92, color: tornMetal }); // front coaming
      boxes.push({ x: cp.x - cp.w * 0.46, y: cp.y - 0.05, z: 0, w: 0.2, h: 0.3, d: cp.d * 0.86, color: charBlack }); // rear coaming
      boxes.push({ x: cp.x, y: cp.y - 0.18, z: 0, w: cp.w * 0.7, h: 0.22, d: cp.d * 0.7, color: blastHole }); // dark open tub
      const shards = 9;
      for (let i = 0; i < shards; i++) {
        const sgn = i % 2 ? 1 : -1;
        const along = (i / shards - 0.5) * cp.w;
        boxes.push({
          x: cp.x + along + jit(0.12),
          y: cp.y + 0.08 + rnd() * 0.28,
          z: sgn * rim * (0.82 + rnd() * 0.26),
          w: 0.12 + rnd() * 0.16,
          h: 0.24 + rnd() * 0.3,
          d: 0.06,
          color: i % 3 === 0 ? canopyGlint : glassDeep, // jagged glass teeth
        });
      }
    } else {
      // Blown-out, darkened glass sitting proud of the frame.
      boxes.push({ x: cp.x, y: cp.y + 0.08, z: 0, w: cp.w * 0.95, h: 0.18, d: cp.d * 0.95, color: shatterGlass });
      // The hole the round punched through.
      boxes.push({ x: cp.x + jit(cp.w * 0.3), y: cp.y + 0.13, z: jit(cp.d * 0.25), w: cp.w * 0.32, h: 0.22, d: cp.d * 0.32, color: blastHole });
      // A spider-web of fractures — dark splits plus a few pale glints so it
      // reads as shattered glass, not just a dark panel.
      const cracks = 4 + Math.round(pilotv * 4);
      for (let i = 0; i < cracks; i++) {
        const col = i % 3 === 0 ? canopyGlint : charBlack;
        if (i % 2 === 0) {
          boxes.push({ x: cp.x + jit(cp.w * 0.6), y: cp.y + 0.17, z: jit(cp.d * 0.35), w: cp.w * (0.3 + rnd() * 0.6), h: 0.05, d: 0.05, color: col });
        } else {
          boxes.push({ x: cp.x + jit(cp.w * 0.4), y: cp.y + 0.17, z: jit(cp.d * 0.45), w: 0.05, h: 0.05, d: cp.d * (0.3 + rnd() * 0.6), color: col });
        }
      }
      // A couple of jagged shards still standing in the frame.
      for (let i = 0; i < 3; i++) {
        boxes.push({ x: cp.x + jit(cp.w * 0.7), y: cp.y + 0.22 + rnd() * 0.12, z: jit(cp.d * 0.5), w: 0.1 + rnd() * 0.14, h: 0.14 + rnd() * 0.16, d: 0.06, color: i % 2 ? shatterGlass : tornMetal });
      }
    }
  }

  // Bullet pocks (roadmap #8): a deterministic scatter of small dark holes over
  // each battle-damaged component. Independent of the scorch/tear overlay above
  // — this is the fine texture of getting shot up, and it clears automatically
  // when a deck repair climbs the subsystem bytes back. Each hole is seeded by
  // (entityId, component, index) so it lands identically on every client and
  // accretes beside its neighbours as the component takes more fire.
  const HOLES = 7; // per subsystem at zero health
  const holes = (comp, health, place) => {
    const n = Math.floor((1 - clamp01(health ?? 1)) * HOLES);
    for (let i = 0; i < n; i++) {
      const r = mulberry32(holeSeed(entityId, comp, i));
      const s = 0.12 + r() * 0.08;
      const p = place(r);
      boxes.push({ x: p.x, y: p.y, z: p.z, w: s, h: s, d: s, color: blastHole });
    }
  };
  const eA = a.engine, wA = a.wing, fA = a.fuse, tA = a.tank, cA = a.canopy;
  holes(1, d.engine, (r) => ({ // engine cowl / nacelle
    x: eA.x + (r() - 0.5) * 0.9, y: eA.y + (r() - 0.5) * eA.r * 1.4, z: eA.z + (r() - 0.5) * eA.r * 1.6,
  }));
  for (const [comp, health, side] of [[3, d.wingR, 1], [2, d.wingL, -1]]) {
    holes(comp, health, (r) => ({
      x: wA.x + (r() - 0.5) * wA.chord * 1.4,
      y: wA.y + (r() - 0.5) * wA.thick * 1.3,
      z: side * (0.9 + r() * (wA.tipZ * 0.95 - 0.9)),
    }));
  }
  holes(4, d.fuel, (r) => ({ // punctured tank / belly
    x: tA.x - r() * tA.len, y: tA.y + (r() - 0.5) * 0.3, z: (r() - 0.5) * fA.z * 1.3,
  }));
  holes(5, d.pilot, (r) => ({ // cockpit surround
    x: cA.x + (r() - 0.5) * cA.w, y: cA.y + (r() - 0.4) * 0.4, z: (r() - 0.5) * cA.d,
  }));
  const tlA = a.tail;
  holes(6, d.tail, (r) => ({ // tailplane + fin root
    x: tlA.x + (r() - 0.5) * 1.4,
    y: tlA.y + r() * (tlA.finY - tlA.y) * 0.5,
    z: (r() - 0.5) * tlA.spanZ * 1.7,
  }));
  // Structural (airframe HP): general fuselage, a handful scaling with the hp
  // bucket (capped shorter than a subsystem — the fuselage is broad but tough).
  const hpN = Math.floor((1 - clamp01(d.hp ?? 1)) * 4);
  for (let i = 0; i < hpN; i++) {
    const r = mulberry32(holeSeed(entityId, 0, i));
    const s = 0.13 + r() * 0.08;
    const sd = r() > 0.5 ? 1 : -1;
    boxes.push({ x: lerpN(fA.x0, fA.x1, r()), y: fA.y + (r() - 0.5) * fA.h, z: sd * (fA.z + 0.02), w: s, h: s, d: s, color: blastHole });
  }

  return boxes;
}

// Earnable paint schemes (roadmap #25). The config array is handed in at boot
// via setSchemes(); each entry restyles the fuselage/trim and optionally paints
// a fin stripe. The team wing/tail hue (t.color) is NEVER touched, so friend/foe
// reads at all ranges no matter the paint.
let SCHEMES = [];
export function setSchemes(list) {
  SCHEMES = Array.isArray(list) ? list : [];
}

// A per-class fin box the tail stripe paints when a scheme sets one. Reuses each
// builder's fin coordinates so the stripe sits on the actual rudder/fin (the
// Ironclad has twin fins, hence two boxes).
const TAIL_STRIPE_ANCHORS = [
  [{ x: -4.42, y: 1.12, z: 0, w: 0.5, h: 0.44, d: 0.34 }],                                  // 0 Sparrowhawk
  [{ x: -4.78, y: 1.18, z: 0, w: 0.52, h: 0.44, d: 0.3 }],                                  // 1 Thunderhead
  [{ x: -4.55, y: 1.3, z: 2.7, w: 1.05, h: 0.44, d: 0.36 }, { x: -4.55, y: 1.3, z: -2.7, w: 1.05, h: 0.44, d: 0.36 }], // 2 Ironclad (twin fins)
  [{ x: -6.3, y: 1.75, z: 0, w: 0.72, h: 0.5, d: 0.42 }],                                   // 3 Leviathan
  [{ x: -4.75, y: 1.55, z: 0, w: 0.6, h: 0.5, d: 0.3 }],                                    // 4 Swordfin
  [{ x: -4.72, y: 1.62, z: 0, w: 0.52, h: 0.5, d: 0.26 }],                                  // 5 Starfang
];

// A shallow copy of a team palette with the scheme's body/accent overrides
// applied. color (the team hue) is deliberately left alone.
function applyScheme(team, schemeDef) {
  const base = TEAM_INFO[team] ?? TEAM_INFO[0];
  if (!schemeDef) return base;
  const t = { ...base };
  if (schemeDef.body) t.body = new THREE.Color(schemeDef.body).getHex();
  if (schemeDef.accent) t.accent = new THREE.Color(schemeDef.accent).getHex();
  return t;
}

// The box list for a scheme's fin stripe, or [] when the scheme paints none.
function tailStripeBoxes(type, schemeDef) {
  if (!schemeDef || !schemeDef.tailStripe) return [];
  const color = new THREE.Color(schemeDef.tailStripe).getHex();
  const anchors = TAIL_STRIPE_ANCHORS[type] ?? [];
  return anchors.map((b) => ({ ...b, color }));
}

const planeGeoCache = new Map(); // `${team}:${type}:${scheme}` -> geometry

// Exported so the living-airbase parked-aircraft models (roadmap #43) can build
// a plane hull; the scheme arg defaults to 0 (factory) for those callers.
export function planeGeometry(team, type, scheme = 0) {
  const key = `${team}:${type}:${scheme}`;
  if (planeGeoCache.has(key)) return planeGeoCache.get(key);
  const builder = CLASS_BUILDERS[type] ?? CLASS_BUILDERS[0];
  const def = SCHEMES[scheme];
  const boxes = builder(applyScheme(team, def)).concat(tailStripeBoxes(type, def));
  const geo = mergeBoxes(boxes);
  planeGeoCache.set(key, geo);
  return geo;
}

let propGeo = null;
function propellerGeometry() {
  if (propGeo) return propGeo;
  propGeo = mergeBoxes([
    { x: 0, y: 0, z: 0, w: 0.12, h: 3.4, d: 0.4, color: 0x17181c },
    { x: 0, y: 0, z: 0, w: 0.12, h: 0.4, d: 3.4, color: 0x17181c },
    // Safety-yellow blade tips, period style.
    { x: 0, y: 1.62, z: 0, w: 0.13, h: 0.34, d: 0.42, color: 0xf4c542 },
    { x: 0, y: -1.62, z: 0, w: 0.13, h: 0.34, d: 0.42, color: 0xf4c542 },
    { x: 0, y: 0, z: 1.62, w: 0.13, h: 0.42, d: 0.34, color: 0xf4c542 },
    { x: 0, y: 0, z: -1.62, w: 0.13, h: 0.42, d: 0.34, color: 0xf4c542 },
  ]);
  return propGeo;
}

// Faint spinning prop disc; opacity follows throttle in update().
let discGeo = null;
function propDiscGeometry() {
  if (discGeo) return discGeo;
  discGeo = new THREE.CylinderGeometry(1.78, 1.78, 0.06, 12);
  discGeo.rotateZ(Math.PI / 2); // axis along +X like the crankshaft
  return discGeo;
}

// Afterburner plume: a unit cone with its base at the origin (the nozzle)
// tapering to a point one unit aft along -X; PlaneVisual scales it with the
// throttle. Open-ended so there's no cap disc sitting across the nozzle.
let plumeGeo = null;
function afterburnerGeometry() {
  if (plumeGeo) return plumeGeo;
  plumeGeo = new THREE.ConeGeometry(0.5, 1, 12, 1, true);
  plumeGeo.translate(0, 0.5, 0); // base at y=0, apex at y=1
  plumeGeo.rotateZ(Math.PI / 2); // apex swings onto -X: base 0 .. tip -1
  return plumeGeo;
}

// One shock diamond: a unit octahedron, stretched and strung down the plume
// axis by PlaneVisual to fake the standing Mach-disc interference pattern a
// real afterburner paints inside its exhaust.
let diamondGeo = null;
function shockDiamondGeometry() {
  if (diamondGeo) return diamondGeo;
  diamondGeo = new THREE.OctahedronGeometry(1);
  return diamondGeo;
}

// Crew-turret sub-geometries (team-independent), cached by part key.
const turretGeoCache = new Map();
function turretGeometry(key, build) {
  let geo = turretGeoCache.get(key);
  if (!geo) {
    geo = mergeBoxes(build());
    turretGeoCache.set(key, geo);
  }
  return geo;
}
const turretFlashGeo = new THREE.BoxGeometry(1.0, 0.3, 0.3);

const planeMaterial = new THREE.MeshLambertMaterial({ vertexColors: true });
// Battle-damage overlay shares the lit, vertex-colored look of the airframe but
// is pulled slightly toward the camera so scorch/holes win the depth test
// against the body panels they sit on instead of z-fighting them.
const damageMaterial = new THREE.MeshLambertMaterial({
  vertexColors: true,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -1,
});
const flashMaterial = new THREE.MeshBasicMaterial({ color: 0xffd76b });
const flashGeo = new THREE.BoxGeometry(1.3, 0.42, 0.42);

// Banner cloth for the ad circus (CLASS_BANNERS): the message drawn onto a
// canvas texture, sized to fit however long the sponsor rambles. Two looks:
// "towed" is white cloth with poster-red letters and red edge tapes (the
// classic beach-plane banner); "sides" is the zeppelin's framed hull panel.
function makeBannerTexture(text, kind) {
  const canvas = document.createElement("canvas");
  // Aspect ratios match the meshes they map onto (see _buildBanner): the
  // towed cloth is a long ribbon, the hull panel a squatter board.
  canvas.width = 2048;
  canvas.height = kind === "sides" ? 512 : 320;
  const W = canvas.width, H = canvas.height;
  const ctx = canvas.getContext("2d");
  const msg = (text || "").toUpperCase();
  if (kind === "sides") {
    ctx.fillStyle = "#f2ead2"; // painted panel on the gasbag
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#c8452e";
    ctx.lineWidth = 24;
    ctx.strokeRect(18, 18, W - 36, H - 36);
    ctx.fillStyle = "#1d2a4a";
  } else {
    ctx.fillStyle = "#f5f0e0"; // towed cloth
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#c8452e";
    ctx.fillRect(0, 0, W, 28); // edge tapes
    ctx.fillRect(0, H - 28, W, 28);
  }
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Shrink to fit: long copy gets smaller letters, not a clipped slogan.
  let size = Math.floor(H * 0.56);
  do {
    ctx.font = `bold ${size}px 'Courier New', monospace`;
    size -= 8;
  } while (size > 40 && ctx.measureText(msg).width > W - 120);
  ctx.fillText(msg, W / 2, H / 2 + 4);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function makeNameplate(name, team, nemesis = false, title = "") {
  const titled = !!title;
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = titled ? 84 : 56;
  const ctx = canvas.getContext("2d");
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Callsign row.
  ctx.font = "bold 34px 'Courier New', monospace";
  const w = Math.min(250, ctx.measureText(name).width + 24);
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  ctx.fillRect(128 - w / 2, 4, w, 48);
  // Your nemesis reads orange for you and you alone (roadmap #21); the
  // teamless ad circus (team 2, adfleet.go) signs its pilots in circus gold.
  ctx.fillStyle = nemesis ? "#ff9a3a" : team === TEAM_CRIMSON ? "#ff7a7a" : team === 1 ? "#7aa6ff" : "#f4c542";
  ctx.fillText(name, 128, 28);
  // Career title row, a dimmer team shade under the callsign (roadmap #26).
  if (titled) {
    ctx.font = "bold 22px 'Courier New', monospace";
    const tw = Math.min(252, ctx.measureText(title).width + 20);
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.fillRect(128 - tw / 2, 55, tw, 26);
    ctx.fillStyle = team === TEAM_CRIMSON ? "#c87a7a" : "#7a96c8";
    ctx.fillText(title, 128, 69);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, depthTest: true, transparent: true }),
  );
  sprite.scale.set(14, titled ? 4.6 : 3.1, 1);
  sprite.position.set(0, titled ? 4.4 : 4, 0);
  return sprite;
}

// Ace markers (roadmap #26): a gold spinner cap (team-independent) and a small
// team-colored fin pennant, both cached and cheap. Geometry only — no emissive,
// so they never light up as a night targeting beacon.
let aceSpinnerGeo = null;
function aceSpinner() {
  if (!aceSpinnerGeo) {
    aceSpinnerGeo = mergeBoxes([
      { x: 0, y: 0, z: 0, w: 0.5, h: 0.62, d: 0.62, color: 0xd8b84a },
      { x: 0.32, y: 0, z: 0, w: 0.34, h: 0.34, d: 0.34, color: 0xe8cf78 },
    ]);
  }
  return aceSpinnerGeo;
}
const acePennantGeoCache = new Map();
function acePennant(team) {
  let geo = acePennantGeoCache.get(team);
  if (!geo) {
    const t = TEAM_INFO[team] ?? TEAM_INFO[0];
    geo = mergeBoxes([
      { x: 0, y: 0, z: 0, w: 0.12, h: 0.42, d: 0.5, color: 0xd8b84a }, // gold staff
      { x: -0.55, y: 0.12, z: 0, w: 1.0, h: 0.34, d: 0.06, color: t.color }, // team flag
    ]);
    acePennantGeoCache.set(team, geo);
  }
  return geo;
}

// --- losing whole sections (advanced damage) --------------------------------
// The body is one merged mesh and the wing is a single full-span slab, so a
// section can't just be hidden. Instead we build a one-off instance body with
// the lost boxes trimmed/dropped, and shed the piece into the world.

// Trim every box so nothing on side `s` (+1 right / -1 left) reaches past the
// shear line; boxes wholly outboard of it are dropped. Returns a new list.
function clipWing(boxes, type, s) {
  const breakAt = wingBreakAt(type);
  const out = [];
  for (const b of boxes) {
    const hd = (b.cone ? Math.max(b.r0 ?? 0, b.r1 ?? 0) : b.d / 2);
    const u0 = s * b.z; // piece centre projected onto the cut axis
    const inner = u0 - hd;
    const outer = u0 + hd;
    if (inner >= breakAt) continue; // wholly outboard — sheared away
    if (outer > breakAt) {
      if (b.cone) continue; // a cone can't be trimmed: the whole pod shears off
      const uNew = (inner + breakAt) / 2;
      out.push({ ...b, d: breakAt - inner, z: s * uNew });
    } else {
      out.push(b);
    }
  }
  return out;
}

// Shear the whole empennage off: drop every flying surface aft of the break
// (the wide tailplane slabs and anything up the fin), keeping the boom, the
// tailwheel and — on the jet — the burner can, so the wreck still reads as
// a fuselage that lost its feathers.
function clipTail(boxes, type) {
  const tl = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).tail;
  const cut = tl.x + 0.55;
  return boxes.filter((b) => {
    if (b.x >= cut) return true;
    const zReach = Math.abs(b.z ?? 0) + (b.d ?? 0) / 2;
    return !(b.y > 0.75 || zReach > 1.0); // flying surfaces only
  });
}

// Drop the canopy glass near the cockpit and leave a dark, open tub.
function stripCanopy(boxes, type) {
  const cp = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).canopy;
  const out = boxes.filter(
    (b) => !((b.color === glass || b.color === glassDeep) && Math.abs(b.x - cp.x) < cp.w * 0.85 && b.y > 0.45),
  );
  out.push({ x: cp.x, y: cp.y - 0.18, z: 0, w: cp.w * 0.66, h: 0.36, d: cp.d * 0.66, color: 0x0b0d0f });
  return out;
}

// Instance body geometry with the lost sections sheared off (cached per combo).
function damagedBody(team, type, lost, scheme = 0) {
  const key = `${team}:${type}:${scheme}:x${lost.wingL ? "L" : ""}${lost.wingR ? "R" : ""}${lost.canopy ? "C" : ""}${lost.tail ? "T" : ""}`;
  const hit = planeGeoCache.get(key);
  if (hit) return hit;
  const def = SCHEMES[scheme];
  let boxes = (CLASS_BUILDERS[type] ?? CLASS_BUILDERS[0])(applyScheme(team, def)).concat(tailStripeBoxes(type, def));
  if (lost.wingR) boxes = clipWing(boxes, type, 1);
  if (lost.wingL) boxes = clipWing(boxes, type, -1);
  if (lost.canopy) boxes = stripCanopy(boxes, type);
  if (lost.tail) boxes = clipTail(boxes, type);
  const geo = mergeBoxes(boxes);
  planeGeoCache.set(key, geo);
  return geo;
}

// Geometry for the torn-off pieces, centred on their own origin so they tumble.
function wingtipDebrisGeo(team, type) {
  const key = `${team}:${type}:wtd`;
  const hit = planeGeoCache.get(key);
  if (hit) return hit;
  const w = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).wing;
  const t = TEAM_INFO[team];
  const len = w.tipZ * (1 - WING_KEEP);
  const geo = mergeBoxes([
    { x: w.x, y: w.y, z: 0, w: w.chord * 1.8, h: w.thick, d: len, color: t.color },
    { x: w.x, y: w.y + 0.05, z: 0, w: w.chord, h: 0.1, d: w.chord, color: t.accent },
    { x: w.x, y: w.y, z: len * 0.42, w: w.chord * 1.4, h: w.thick * 1.05, d: 0.5, color: t.accent },
    { x: w.x - w.chord * 0.7, y: w.y, z: -len * 0.45, w: 0.5, h: w.thick, d: 0.5, color: charBlack },
  ]);
  planeGeoCache.set(key, geo);
  return geo;
}

// The torn-off empennage: tailplane cross with a fin stub, tumbling aft.
function tailDebrisGeo(team, type) {
  const key = `${team}:${type}:tld`;
  const hit = planeGeoCache.get(key);
  if (hit) return hit;
  const tl = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).tail;
  const t = TEAM_INFO[team];
  const finH = Math.max(0.8, (tl.finY - tl.y) * 0.9);
  const geo = mergeBoxes([
    { x: 0, y: 0, z: 0, w: 1.3, h: 0.24, d: tl.spanZ * 2, color: t.color }, // tailplane
    { x: -0.45, y: 0, z: 0, w: 0.4, h: 0.2, d: tl.spanZ * 1.8, color: shade(t.color, 0.78) }, // elevator
    { x: -0.15, y: finH * 0.5, z: 0, w: 0.9, h: finH, d: 0.26, color: t.body }, // fin stub
    { x: 0.55, y: 0.05, z: 0, w: 0.4, h: 0.5, d: 0.5, color: charBlack }, // charred break
  ]);
  planeGeoCache.set(key, geo);
  return geo;
}

function canopyDebrisGeo(team, type) {
  const key = `${team}:${type}:cpd`;
  const hit = planeGeoCache.get(key);
  if (hit) return hit;
  const cp = (DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0]).canopy;
  const t = TEAM_INFO[team];
  const geo = mergeBoxes([
    { x: 0, y: 0, z: 0, w: cp.w * 0.66, h: 0.36, d: cp.d * 0.66, color: glass },
    { x: 0, y: 0.24, z: 0, w: cp.w * 0.46, h: 0.12, d: cp.d * 0.46, color: glassDeep },
    // A dark frame ring + charred sill so the torn-off hood reads against sky.
    { x: cp.w * 0.34, y: 0, z: 0, w: 0.18, h: 0.46, d: cp.d * 0.78, color: t.body },
    { x: -cp.w * 0.34, y: 0, z: 0, w: 0.18, h: 0.46, d: cp.d * 0.78, color: t.body },
    { x: 0, y: -0.22, z: 0, w: cp.w * 0.74, h: 0.16, d: cp.d * 0.74, color: charBlack },
  ]);
  planeGeoCache.set(key, geo);
  return geo;
}

// World-space tumbling wreckage shed when a section tears off.
const _debris = [];
const _gravity = new THREE.Vector3(0, -9.81, 0);

function shedDebris(scene, geo, pos, quat, vel) {
  const mesh = new THREE.Mesh(geo, planeMaterial);
  mesh.position.copy(pos);
  mesh.quaternion.copy(quat);
  scene.add(mesh);
  _debris.push({
    scene,
    mesh,
    vel: vel.clone(),
    spin: new THREE.Vector3((Math.random() - 0.5) * 7, (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 9),
    life: 0,
  });
}

/** Advance and retire shed wreckage; call once per frame from the render loop. */
export function updateDebris(dt) {
  for (let i = _debris.length - 1; i >= 0; i--) {
    const d = _debris[i];
    d.life += dt;
    d.vel.addScaledVector(_gravity, dt);
    d.mesh.position.addScaledVector(d.vel, dt);
    d.mesh.rotation.x += d.spin.x * dt;
    d.mesh.rotation.y += d.spin.y * dt;
    d.mesh.rotation.z += d.spin.z * dt;
    if (d.life >= 4.5) {
      d.scene.remove(d.mesh);
      _debris.splice(i, 1);
    }
  }
}

/** A pre-built tumbling section piece for the screenshot director (no physics). */
export function makeSectionDebris(team, type, kind) {
  const geo = kind === "canopy" ? canopyDebrisGeo(team, type)
    : kind === "tail" ? tailDebrisGeo(team, type)
    : wingtipDebrisGeo(team, type);
  return new THREE.Mesh(geo, planeMaterial);
}

/** Local-space anchor where a section shears (for placing debris). */
export function sectionBreakPoint(type, kind) {
  const a = DAMAGE_ANCHORS[type] ?? DAMAGE_ANCHORS[0];
  if (kind === "canopy") return new THREE.Vector3(a.canopy.x, a.canopy.y, 0);
  if (kind === "tail") return new THREE.Vector3(a.tail.x, a.tail.y, 0);
  const s = kind === "wingL" ? -1 : 1;
  return new THREE.Vector3(a.wing.x, a.wing.y, s * wingBreakAt(type));
}

/** A renderable plane: blocky body, spinning props, wing-gun muzzle flashes. */
export class PlaneVisual {
  constructor(scene, team, name, isSelf, type = 0, scheme = 0, ad = "") {
    this.scene = scene;
    this.team = team;
    this.name = name;
    this.isSelf = isSelf;
    this.scheme = scheme | 0; // equipped paint scheme index (roadmap #25)
    this.title = "";          // career title on the nameplate (roadmap #26)
    this._nemesis = false;    // orange callsign for the local viewer (roadmap #21)
    this._ace = false;        // gold spinner + fin pennant (roadmap #26)
    this.ad = ad || "";       // banner copy for the ad circus (adfleet.go)
    this._bannerT = Math.random() * 10; // desync the cloth flutter per craft
    this.group = new THREE.Group();
    scene.add(this.group);
    // Seeds the deterministic bullet-hole scatter (roadmap #8); set by main.js
    // to the snapshot entity id so every client pocks the airframe identically.
    this.entityId = 0;
    this.buildType(type);
    this.throttle = 0.7;
  }

  buildType(type) {
    this.type = type;
    this.group.clear(); // also drops the old damage overlay
    this.damage = null;
    this._dmgSig = null;
    this._lostKey = undefined; // no sections lost on a fresh airframe
    this.killMarks = null; // rebuilt on demand by setKillMarks
    this._killN = -1;
    this.discMat?.dispose();
    this.body = new THREE.Mesh(planeGeometry(this.team, type, this.scheme), planeMaterial);
    this.group.add(this.body);

    this.discMat = new THREE.MeshBasicMaterial({
      color: 0x33363d,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
    });
    this.props = [];
    for (const [px, pz] of CLASS_PROPS[type] ?? CLASS_PROPS[0]) {
      const prop = new THREE.Mesh(propellerGeometry(), planeMaterial);
      prop.position.set(px, 0, pz);
      this.props.push(prop);
      this.group.add(prop);
      const disc = new THREE.Mesh(propDiscGeometry(), this.discMat);
      disc.position.set(px + 0.1, 0, pz);
      this.group.add(disc);
    }

    this.flashes = [];
    for (const [mx, my, mz] of CLASS_MUZZLES[type] ?? CLASS_MUZZLES[0]) {
      const flash = new THREE.Mesh(flashGeo, flashMaterial);
      flash.position.set(mx + 0.8, my, mz);
      flash.visible = false;
      this.flashes.push(flash);
      this.group.add(flash);
    }

    // Jet exhaust: an additive afterburner cone off the tailpipe whose
    // length and glow ride the throttle (jets carry no propeller), with a
    // string of white-hot shock diamonds standing inside the plume.
    this.jetMat?.dispose();
    this.jetCoreMat?.dispose();
    this.jet = null;
    this.jetMat = null;
    this.jetCoreMat = null;
    const pipe = CLASS_JETPIPES[type];
    if (pipe) {
      // Normal (not additive) blending: additive washes to white against a
      // bright sky and the diamonds vanish; a translucent orange cone with
      // near-opaque cream diamonds drawn over it reads in any light.
      this.jetMat = new THREE.MeshBasicMaterial({
        color: 0xff8a2e,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        fog: false,
      });
      this.jetCoreMat = new THREE.MeshBasicMaterial({
        color: 0xffe9b0,
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
        fog: false,
      });
      // Unit-space plume group (base at the nozzle, tip 1 unit aft): update()
      // stretches the whole group, so the diamonds ride the flame's length.
      this.jet = new THREE.Group();
      const plume = new THREE.Mesh(afterburnerGeometry(), this.jetMat);
      plume.renderOrder = 1;
      this.jet.add(plume);
      this.jetDiamonds = [];
      const N = 6;
      for (let i = 0; i < N; i++) {
        const f = (i + 0.55) / (N + 0.6); // fraction down the plume
        const d = new THREE.Mesh(shockDiamondGeometry(), this.jetCoreMat);
        d.position.x = -f;
        const r = 0.38 * (1 - f * 0.8); // diamonds shrink toward the tip
        d.scale.set(0.085, r, r);
        d.renderOrder = 2; // always over the plume cone they stand inside
        this.jetDiamonds.push(d);
        this.jet.add(d);
      }
      this.jet.position.set(pipe.pos[0], pipe.pos[1], pipe.pos[2]);
      this.group.add(this.jet);
    }

    // Crewable defensive turrets (bombers only): a yaw ring carrying a
    // pitching twin-gun cradle, eased toward the aim the snapshots (or the
    // local gunner's prediction) report, stowed when the seat is empty.
    this.turrets = [];
    for (const spec of CLASS_TURRETS[type] ?? []) {
      const yawPivot = new THREE.Group();
      yawPivot.position.set(spec.pos[0], spec.pos[1], spec.pos[2]);
      const dome = new THREE.Mesh(turretGeometry(`${spec.kind}:dome`, () => turretDomeBoxes(spec.kind)), planeMaterial);
      yawPivot.add(dome);
      const pitchPivot = new THREE.Group();
      yawPivot.add(pitchPivot);
      const guns = new THREE.Mesh(
        turretGeometry(`${spec.kind}:guns:${spec.barrelLen}`, () => turretGunBoxes(spec.kind, spec.barrelLen)),
        planeMaterial,
      );
      pitchPivot.add(guns);
      const gy = spec.kind === "top" ? 0.12 : 0.05;
      const flashes = [];
      for (const z of [0.24, -0.24]) {
        const flash = new THREE.Mesh(turretFlashGeo, flashMaterial);
        flash.position.set(spec.barrelLen + 0.5, gy, z);
        flash.visible = false;
        flashes.push(flash);
        pitchPivot.add(flash);
      }
      const tur = {
        spec, yawPivot, pitchPivot, flashes,
        yaw: spec.stowYaw, pitch: spec.stowPitch,
        tgtYaw: spec.stowYaw, tgtPitch: spec.stowPitch,
        manned: false, firing: false,
      };
      yawPivot.rotation.y = tur.yaw;
      pitchPivot.rotation.z = tur.pitch;
      this.turrets.push(tur);
      this.group.add(yawPivot);
    }

    this.nameplate = null;
    if (!this.isSelf) {
      this.nameplate = makeNameplate(this.name, this.team, this._nemesis, this.title);
      this.group.add(this.nameplate);
    }
    // The circus hangs its message (a fresh rebuild wipes the old cloth).
    this.banner = null;
    this._buildBanner();
    // Re-attach the ace markers after a full rebuild (a plane change wipes them).
    this._aceMeshes = null;
    if (this._ace) this._buildAce();
  }

  /** Swap the banner copy in place (the "ad" event at the round bell). */
  setBanner(text) {
    text = text || "";
    if (this.ad === text) return;
    this.ad = text;
    this._buildBanner();
  }

  // Build (or rebuild) the ad banner for this type: a towed cloth trailing
  // the Skywriter on a rope, or a painted panel on each of the Marquee's
  // flanks. Non-circus types, or an empty message, fly clean.
  _buildBanner() {
    if (this.banner) {
      this.group.remove(this.banner);
      this.banner.traverse((o) => {
        o.geometry?.dispose?.();
        if (o.material) {
          o.material.map?.dispose();
          o.material.dispose();
        }
      });
      this.banner = null;
    }
    const spec = CLASS_BANNERS[this.type];
    if (!spec || !this.ad) return;
    this.banner = new THREE.Group();
    if (spec.kind === "towed") {
      // Rope from the tail hook, then the cloth. A real aerial banner dwarfs
      // the plane dragging it — this one is ~3.5 plane-lengths of ribbon.
      // DoubleSide so the reverse reads (mirrored) just like the real thing.
      const rope = new THREE.Mesh(
        new THREE.BoxGeometry(4.2, 0.08, 0.08),
        new THREE.MeshLambertMaterial({ color: 0xd8d2bc }),
      );
      rope.position.set(-6.0, 0.3, 0);
      this.banner.add(rope);
      const cloth = new THREE.Mesh(
        new THREE.PlaneGeometry(32, 5),
        new THREE.MeshLambertMaterial({
          map: makeBannerTexture(this.ad, "towed"),
          side: THREE.DoubleSide,
        }),
      );
      cloth.position.set(-24, 0.3, 0);
      this.banner.add(cloth);
      this._cloth = cloth;
    } else {
      // One panel per flank, each rotated so its face (and the writing)
      // reads correctly from that side of the hull. Sized to sit INSIDE the
      // hull's flat center section (12 long, radius 3.1) — the board mustn't
      // spill onto the tapered nose and tail cones.
      const tex = makeBannerTexture(this.ad, "sides");
      for (const s of [1, -1]) {
        const panel = new THREE.Mesh(
          new THREE.PlaneGeometry(10.5, 2.6),
          new THREE.MeshLambertMaterial({ map: tex }),
        );
        panel.position.set(0, 0.3, s * 3.14);
        panel.rotation.y = s > 0 ? 0 : Math.PI;
        this.banner.add(panel);
      }
      this._cloth = null;
    }
    this.group.add(this.banner);
  }

  /** Hide/show the floating callsign (the local gunner rides right beside it). */
  setNameplateVisible(v) {
    if (this.nameplate) this.nameplate.visible = v;
  }

  // Rebuild the floating callsign in place, preserving visibility. Shared by the
  // nemesis tint (#21) and the career-title line (#26).
  _rebuildNameplate() {
    if (this.isSelf || !this.nameplate) return;
    const vis = this.nameplate.visible;
    this.group.remove(this.nameplate);
    this.nameplate.material.map?.dispose();
    this.nameplate.material.dispose();
    this.nameplate = makeNameplate(this.name, this.team, this._nemesis, this.title);
    this.nameplate.visible = vis;
    this.group.add(this.nameplate);
  }

  /** Tint the callsign orange while this pilot is the viewer's nemesis (roadmap #21). */
  setNemesis(on) {
    on = !!on;
    if (this._nemesis === on) return;
    this._nemesis = on;
    this._rebuildNameplate();
  }

  /** Add/refresh the career-title line under the callsign (roadmap #26). */
  setTitle(title) {
    title = title || "";
    if (this.title === title) return;
    this.title = title;
    this._rebuildNameplate();
  }

  /** Repaint the airframe with a new scheme (roadmap #25), keeping nameplate,
   * markers, props and turrets intact. */
  setScheme(scheme) {
    scheme = scheme | 0;
    if (scheme === this.scheme) return;
    this.scheme = scheme;
    this.body.geometry = planeGeometry(this.team, this.type, this.scheme);
    this._dmgSig = null;      // force the damage overlay to re-evaluate
    this._lostKey = undefined; // and the section-loss body variant
  }

  /** Stencil kill marks under the cockpit — one per kill this round, capped at
   * 20 (roadmap #25). Rebuilds only when the count changes; culled by distance
   * in update(). */
  setKillMarks(n) {
    n = Math.max(0, Math.min(20, n | 0));
    if (n === this._killN) return;
    this._killN = n;
    if (this.killMarks) {
      this.group.remove(this.killMarks);
      this.killMarks.geometry.dispose();
      this.killMarks = null;
    }
    if (n <= 0) return;
    const a = DAMAGE_ANCHORS[this.type] ?? DAMAGE_ANCHORS[0];
    const z = a.fuse.z + 0.16; // proud of the starboard fuselage skin so it reads
    const x0 = a.canopy.x + 0.2; // just forward of the cockpit
    const boxes = [];
    const per = 10;
    for (let i = 0; i < n; i++) {
      const row = Math.floor(i / per), col = i % per;
      boxes.push({
        x: x0 - col * 0.28, y: a.fuse.y + 0.28 - row * 0.32, z,
        w: 0.22, h: 0.26, d: 0.05, color: 0x15171b,
      });
    }
    this.killMarks = new THREE.Mesh(mergeBoxes(boxes), damageMaterial);
    this.killMarks.visible = false; // update() reveals it up close
    this.group.add(this.killMarks);
  }

  _buildAce() {
    const a = DAMAGE_ANCHORS[this.type] ?? DAMAGE_ANCHORS[0];
    const spin = new THREE.Mesh(aceSpinner(), planeMaterial);
    spin.position.set(a.engine.x + 0.5, a.engine.y, a.engine.z);
    const fin = (TAIL_STRIPE_ANCHORS[this.type] ?? [])[0] ?? { x: -4.5, y: 1.6, z: 0 };
    const pennant = new THREE.Mesh(acePennant(this.team), planeMaterial);
    pennant.position.set(fin.x, fin.y + 1.15, fin.z);
    this._aceMeshes = [spin, pennant];
    for (const m of this._aceMeshes) {
      m.visible = false; // update() reveals it inside the marker range
      this.group.add(m);
    }
  }

  /** Gold spinner cap + fin pennant marking a career ace (roadmap #26). */
  setAce(on) {
    on = !!on;
    if (on === this._ace) return;
    this._ace = on;
    if (on) {
      this._buildAce();
    } else {
      for (const m of this._aceMeshes ?? []) this.group.remove(m);
      this._aceMeshes = null;
    }
  }

  /**
   * Aim one crew turret from snapshot (or locally predicted) state. Pass
   * rows [{seat, yaw, pitch, firing}] for every manned seat; seats not named
   * stow. Call setTurretAim(seat, yaw, pitch, firing) to drive one seat
   * directly (the local gunner's prediction).
   */
  setTurrets(rows) {
    if (!this.turrets?.length) return;
    for (const t of this.turrets) {
      t.manned = false;
      t.firing = false;
    }
    for (const r of rows ?? []) this.setTurretAim(r.seat, r.yaw, r.pitch, r.firing);
  }

  setTurretAim(seat, yaw, pitch, firing) {
    const t = this.turrets?.[seat];
    if (!t) return;
    t.manned = true;
    t.tgtYaw = yaw;
    t.tgtPitch = pitch;
    t.firing = !!firing;
  }

  setType(type) {
    if (type !== this.type) this.buildType(type);
  }

  update(dt, firing, camDist) {
    // Career detailing (roadmap #25/#26) is a reward you notice after the merge,
    // not a beacon before it: kill marks read only up close, the ace pennant and
    // spinner out to the nameplate's legibility range.
    if (camDist !== undefined) {
      if (this.killMarks) this.killMarks.visible = camDist < 120;
      if (this._aceMeshes) for (const m of this._aceMeshes) m.visible = camDist < 400;
    }
    // The prop answers the ENGINE, not the throttle lever: a shot-out engine
    // windmills lazily in the slipstream no matter how hard the pilot pushes
    // the handle — the spin-down IS the engine-out telling (no HUD needed).
    const eng = this.engineHealth ?? 1;
    const engRun = eng <= 0.02 ? 0.06 : 0.45 + 0.55 * eng;
    for (const prop of this.props) {
      prop.rotation.x += dt * (12 + this.throttle * 55) * engRun;
    }
    // The disc fades with the engine too: a windmilling prop is a faint blur,
    // not the solid ghost of one under power (same engine-out telling).
    this.discMat.opacity = (0.07 + this.throttle * 0.16) * engRun;
    if (this._cloth) {
      // Towed cloth flutters: a lazy ripple, a slight trailing sag. The
      // rotations pivot at the cloth's center, so on a ribbon this long even
      // small angles read as a big, slow wave at the trailing edge.
      this._bannerT += dt;
      this._cloth.rotation.x = Math.sin(this._bannerT * 2.6) * 0.05;
      this._cloth.rotation.y = Math.sin(this._bannerT * 1.7) * 0.035;
      this._cloth.position.y = 0.3 + Math.sin(this._bannerT * 2.1) * 0.25;
    }
    if (this.jet) {
      // Afterburner: the cone stretches aft and flickers with the throttle;
      // the shock diamonds only light once the burner is really cooking.
      // A dying turbojet's flame shrinks with it (same engine-out telling).
      const th = this.throttle * engRun;
      const len = 0.6 + th * (2.6 + Math.random() * 0.7);
      const w = 0.55 + th * 0.65;
      this.jet.scale.set(len, w, w);
      this.jetMat.opacity = 0.2 + th * 0.4;
      this.jetCoreMat.opacity = th > 0.5 ? Math.min(0.95, (th - 0.5) * 2.1) : 0;
      for (const d of this.jetDiamonds ?? []) d.visible = Math.random() > 0.12;
    }
    if (firing !== undefined) {
      // The whole battery fires as one synchronized volley — pair, quad, or
      // lone gatling — so flash every barrel in sync (shared flicker keeps
      // them all lit on the same frames).
      const lit = firing && Math.random() > 0.25;
      for (const f of this.flashes) f.visible = lit;
    }
    // Ease each crew turret toward its aim (a manned target, or the stow
    // position once the seat empties), and flicker the twin muzzles.
    for (const t of this.turrets ?? []) {
      const wantYaw = t.manned ? t.tgtYaw : t.spec.stowYaw;
      const wantPitch = t.manned ? t.tgtPitch : t.spec.stowPitch;
      const k = 1 - Math.exp(-12 * dt);
      t.yaw += Math.atan2(Math.sin(wantYaw - t.yaw), Math.cos(wantYaw - t.yaw)) * k;
      t.pitch += (wantPitch - t.pitch) * k;
      t.yawPivot.rotation.y = t.yaw;
      t.pitchPivot.rotation.z = t.pitch;
      const lit = t.manned && t.firing && Math.random() > 0.35;
      for (const f of t.flashes) f.visible = lit;
    }
  }

  // Show visible battle damage for a component-damage state. Cheap to call
  // every snapshot: it buckets the health values and only rebuilds the overlay
  // mesh when the bucket actually changes. d = { engine, wingL, wingR, hp }.
  applyDamage(d = {}) {
    // The prop and the burner plume read the engine's health every frame
    // (see update()): a dead engine windmills instead of screaming.
    this.engineHealth = clamp01(d.engine ?? 1);
    const q = (v) => Math.round(clamp01(v ?? 1) * 5); // 6 buckets per component
    const sig = `${q(d.engine)}.${q(d.wingL)}.${q(d.wingR)}.${q(d.hp)}.${q(d.fuel)}.${q(d.pilot)}.${q(d.tail)}`;
    if (sig === this._dmgSig) return;
    this._dmgSig = sig;

    // Whole-section losses: swap to a body with the section sheared off, and
    // shed the torn-away piece into the world the moment it goes.
    const lost = {
      wingL: (d.wingL ?? 1) <= WING_LOSE,
      wingR: (d.wingR ?? 1) <= WING_LOSE,
      canopy: (d.pilot ?? 1) <= CANOPY_LOSE,
      tail: (d.tail ?? 1) <= TAIL_LOSE,
    };
    const lostKey = `${lost.wingL ? 1 : 0}${lost.wingR ? 1 : 0}${lost.canopy ? 1 : 0}${lost.tail ? 1 : 0}`;
    if (lostKey !== this._lostKey) {
      const any = lost.wingL || lost.wingR || lost.canopy || lost.tail;
      this.body.geometry = any ? damagedBody(this.team, this.type, lost, this.scheme) : planeGeometry(this.team, this.type, this.scheme);
      // Shed only on a fresh tear (skip the first apply, so spawning or staging
      // an already-damaged plane doesn't fling phantom wreckage).
      const prev = this._lostKey;
      if (prev !== undefined) {
        this.group.updateMatrixWorld();
        if (lost.wingR && prev[1] === "0") this._shed("wingR");
        if (lost.wingL && prev[0] === "0") this._shed("wingL");
        if (lost.canopy && prev[2] === "0") this._shed("canopy");
        if (lost.tail && (prev[3] ?? "0") === "0") this._shed("tail");
      }
      this._lostKey = lostKey;
    }

    if (this.damage) {
      this.group.remove(this.damage);
      this.damage.geometry.dispose();
      this.damage = null;
    }
    const boxes = damageBoxes(this.type, d, this.entityId ?? 0);
    if (!boxes.length) return;
    this.damage = new THREE.Mesh(mergeBoxes(boxes), damageMaterial);
    this.group.add(this.damage);
  }

  // Tear a section off into tumbling world wreckage, kicked clear of the plane.
  _shed(kind) {
    const local = sectionBreakPoint(this.type, kind);
    const pos = this.group.localToWorld(local.clone());
    const axis = kind === "canopy"
      ? new THREE.Vector3(0, 1, 0)
      : kind === "tail"
        ? new THREE.Vector3(-1, 0.4, 0) // the empennage falls away aft
        : new THREE.Vector3(0, 0, kind === "wingL" ? -1 : 1);
    const vel = axis.applyQuaternion(this.group.quaternion).multiplyScalar(6).add(new THREE.Vector3(0, -2, 0));
    const geo = kind === "canopy"
      ? canopyDebrisGeo(this.team, this.type)
      : kind === "tail"
        ? tailDebrisGeo(this.team, this.type)
        : wingtipDebrisGeo(this.team, this.type);
    shedDebris(this.scene, geo, pos, this.group.quaternion.clone(), vel);
  }

  setBlink(on) {
    // Spawn-protection shimmer.
    this.body.visible = !on || Math.floor(performance.now() / 90) % 2 === 0;
  }

  dispose(scene) {
    scene.remove(this.group);
    this.damage?.geometry.dispose();
    this.discMat?.dispose();
    this.jetMat?.dispose();
    this.jetCoreMat?.dispose();
    this.group.traverse((o) => {
      if (o.isSprite) {
        o.material.map?.dispose();
        o.material.dispose();
      }
    });
  }
}
